import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { useTeamDashboard } from '../TeamDashboardContext';
import { apiGet, apiPost, apiPut } from '../api';
import { useWebSocket } from '@/services/useWebSocket';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import apiClient from '../../../../services/apiClient';
import './discussion.css';

type DiscTab = 'chat' | 'roadmap' | 'ai' | 'whiteboard' | 'sandbox';
type WbTool = 'select' | 'pen' | 'line' | 'rect' | 'circle' | 'arrow' | 'text' | 'eraser' | 'sticky';
type RoadmapColId = 'backlog' | 'progress' | 'review' | 'done';

interface RoadmapItem {
  id?: string;
  title: string;
  tag: string;
  tagC: 'g' | 'a' | 'r' | 'b' | 'n';
  assignee: string;
  priority?: 'high' | 'medium' | 'low';
  desc?: string;
  time?: string;
}

interface RoadmapCol {
  id: RoadmapColId;
  title: string;
  color: string;
  items: RoadmapItem[];
}

interface ChatMsg { initials: string; name: string; text: string; time: string; self?: boolean; images?: string[]; }
interface AiMsg { role: 'bot' | 'user'; text: React.ReactNode; }
interface StrokeItem { id: string; tool: WbTool; color: string; size: number; points: { x: number; y: number }[]; text?: string; parentId?: string; connectFrom?: string; connectTo?: string; connectFromEdge?: string; connectToEdge?: string; }
interface StickyNote { id: number; x: number; y: number; text: string; color: string; }
interface ActivityItem {
  id: string;
  memberId?: string;
  memberInitials?: string;
  memberName?: string;
  action?: string;
  detail?: string;
  type?: string;
  timestamp?: string;
}

const AiMarkdown: React.FC<{ content: string }> = ({ content }) => {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        h1: ({ children }) => <h1 className="ai-md-h1">{children}</h1>,
        h2: ({ children }) => <h2 className="ai-md-h2">{children}</h2>,
        h3: ({ children }) => <h3 className="ai-md-h3">{children}</h3>,
        p: ({ children }) => <p className="ai-md-p">{children}</p>,
        ul: ({ children }) => <ul className="ai-md-ul">{children}</ul>,
        ol: ({ children }) => <ol className="ai-md-ol">{children}</ol>,
        li: ({ children }) => <li className="ai-md-li">{children}</li>,
        code: ({ className, children }) => {
          const match = /language-(\w+)/.exec(className || '');
          const value = String(children).replace(/\n$/, '');
          const isInline = !className;
          return isInline ? (
            <code className="ai-md-code-inline">{children}</code>
          ) : (
            <div className="ai-md-code-wrap">
              <div className="ai-md-code-head">{match?.[1] || 'code'}</div>
              <SyntaxHighlighter
                style={oneDark as any}
                language={match?.[1] || 'text'}
                PreTag="div"
              >
                {value}
              </SyntaxHighlighter>
            </div>
          );
        },
        pre: ({ children }) => <div className="ai-md-pre">{children}</div>,
        strong: ({ children }) => <strong className="ai-md-strong">{children}</strong>,
        em: ({ children }) => <em className="ai-md-em">{children}</em>,
        a: ({ href, children }) => <a href={href} target="_blank" rel="noopener noreferrer" className="ai-md-a">{children}</a>,
        blockquote: ({ children }) => <blockquote className="ai-md-blockquote">{children}</blockquote>,
        hr: () => <hr className="ai-md-hr" />,
        table: ({ children }) => <table className="ai-md-table">{children}</table>,
        thead: ({ children }) => <thead className="ai-md-thead">{children}</thead>,
        tbody: ({ children }) => <tbody className="ai-md-tbody">{children}</tbody>,
        tr: ({ children }) => <tr className="ai-md-tr">{children}</tr>,
        th: ({ children }) => <th className="ai-md-th">{children}</th>,
        td: ({ children }) => <td className="ai-md-td">{children}</td>,
      }}
    >
      {content}
    </ReactMarkdown>
  );
};
const cleanChatName = (name: string): string => {
  if (!name) return '';
  const clean = name.trim();
  if (clean.toLowerCase().startsWith('an675') || clean.toLowerCase().startsWith('an453')) {
    return 'Aniket Sharma';
  }
  return clean;
};

const cleanChatInitials = (initials: string, name: string): string => {
  const cleanedName = cleanChatName(name);
  if (cleanedName === 'Aniket Sharma') {
    return 'AS';
  }
  return initials;
};

const INIT_CHAT: ChatMsg[] = [
  { initials: 'AK', name: 'Arjun Kumar', text: 'Sprint 4 kickoff — let\'s aim to close the auth bug by EOD.', time: '9:02 AM' },
  { initials: 'SR', name: 'Sneha Rao', text: 'On it. PR #13 is ready for review whenever you get a chance.', time: '9:08 AM' },
  { initials: 'PK', name: 'Priya Kapoor', text: 'Auth middleware is live on staging. Looks good so far.', time: '9:45 AM' },
  { initials: 'RM', name: 'Rahul Mehta', text: 'Rate limiting is about 60% done. Should have a draft PR by tomorrow.', time: '10:12 AM' },
];

const ROADMAP_COLS: RoadmapCol[] = [
  {
    id: 'backlog',
    title: 'Backlog',
    color: 'var(--text3)',
    items: [
      { title: 'Mobile push notifications', desc: 'FCM + in-app routing', tag: 'feature', tagC: 'b', assignee: 'PK', priority: 'medium', time: '2d' },
      { title: 'Dark mode for client portal', desc: 'theme tokens + QA pass', tag: 'enhancement', tagC: 'n', assignee: 'AK', priority: 'low', time: '1d' },
      { title: 'Multi-tenant support', desc: 'org switching and permissions', tag: 'feature', tagC: 'b', assignee: 'SR', priority: 'high', time: '4d' },
    ],
  },
  {
    id: 'progress',
    title: 'In Progress',
    color: 'var(--amber)',
    items: [
      { title: 'Auth bug fix - session persistence', desc: 'token refresh edge cases', tag: 'bug', tagC: 'r', assignee: 'RM', priority: 'high', time: 'today' },
      { title: 'API refactor - service layer', desc: 'merge service and controller contracts', tag: 'refactor', tagC: 'a', assignee: 'SR', priority: 'medium', time: '3d' },
      { title: 'Payment gateway integration', desc: 'final webhook retries', tag: 'feature', tagC: 'b', assignee: 'PK', priority: 'medium', time: '2d' },
    ],
  },
  {
    id: 'review',
    title: 'In Review',
    color: 'var(--blue)',
    items: [
      { title: 'PR #16 - API refactor', desc: 'awaiting final reviewer', tag: 'review', tagC: 'a', assignee: 'SR', priority: 'medium', time: '4h' },
      { title: 'DB migration scripts', desc: 'rollback verified', tag: 'chore', tagC: 'n', assignee: 'AK', priority: 'low', time: 'done' },
    ],
  },
  {
    id: 'done',
    title: 'Done',
    color: 'var(--green)',
    items: [
      { title: 'Rate limiting on auth endpoints', desc: 'merged to main', tag: 'merged', tagC: 'g', assignee: 'AK', time: 'yesterday' },
      { title: 'Stripe webhook handler', desc: 'monitoring alerts set', tag: 'merged', tagC: 'g', assignee: 'PK', time: '2d ago' },
      { title: 'PR #14 - Payment flow', desc: 'post-merge smoke passed', tag: 'merged', tagC: 'g', assignee: 'PK', time: '3d ago' },
    ],
  },
];

const MBG: Record<string, string> = { AK: 'rgba(77,156,248,.18)', SR: 'rgba(245,166,35,.18)', PK: 'rgba(62,207,142,.18)', RM: 'rgba(242,100,100,.18)' };
const MFG: Record<string, string> = { AK: '#4d9cf8', SR: '#f5a623', PK: '#3ecf8e', RM: '#f26464' };
const WB_COLORS = ['#e8e8f0', '#4d9cf8', '#3ecf8e', '#f5a623', '#f26464', '#b06ef5', '#111111'];

function cloneRoadmapCols(cols: RoadmapCol[]): RoadmapCol[] {
  return cols.map(col => ({
    ...col,
    items: col.items.map(item => ({
      ...item,
      id: item.id || crypto.randomUUID(),
    })),
  }));
}
const COLLAB_CURSORS = [
  { name: 'Priya', color: '#3ecf8e' },
  { name: 'Sneha', color: '#f5a623' },
];

// ─── WHITEBOARD ────────────────────────────────────────────────────────────
function Whiteboard({ isDemo, onStrokeAdded }: { isDemo: boolean; onStrokeAdded?: () => void }) {
  const { teamSlug, members } = useTeamDashboard();
  const { on, sendMessage } = useWebSocket();
  const [liveCursors, setLiveCursors] = useState<Record<string, { x: number; y: number; name: string; color: string }>>({});
  const lastCursorSent = useRef(0);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [tool, setTool] = useState<WbTool>('pen');
  const [color, setColor] = useState('#4d9cf8');
  const [brushSize, setBrushSize] = useState(2);
  const [zoom, setZoom] = useState(100);
  const [strokes, setStrokes] = useState<StrokeItem[]>([]);
  const strokesRef = useRef<StrokeItem[]>([]);
  const [notes, setNotes] = useState<StickyNote[]>([]);
  const [showTemplates, setShowTemplates] = useState(false);

  function applyTemplate(type: 'architecture' | 'database' | 'rest') {
    let newStrokes: StrokeItem[] = [];
    if (type === 'architecture') {
      const r1 = { id: genId(), tool: 'rect' as WbTool, color: '#4d9cf8', size: 2, points: [{ x: 80, y: 100 }, { x: 200, y: 180 }] };
      const t1 = { id: genId(), tool: 'text' as WbTool, color: '#4d9cf8', size: 2, points: [{ x: 105, y: 140 }], text: 'Client App', parentId: r1.id };
      
      const r2 = { id: genId(), tool: 'rect' as WbTool, color: '#3ecf8e', size: 2, points: [{ x: 280, y: 100 }, { x: 400, y: 180 }] };
      const t2 = { id: genId(), tool: 'text' as WbTool, color: '#3ecf8e', size: 2, points: [{ x: 300, y: 140 }], text: 'API Gateway', parentId: r2.id };
      
      const c1 = { id: genId(), tool: 'circle' as WbTool, color: '#f5a623', size: 2, points: [{ x: 480, y: 100 }, { x: 600, y: 220 }] };
      const t3 = { id: genId(), tool: 'text' as WbTool, color: '#f5a623', size: 2, points: [{ x: 505, y: 155 }], text: 'Database', parentId: c1.id };

      const l1 = { id: genId(), tool: 'arrow' as WbTool, color: '#b06ef5', size: 2, points: [{ x: 200, y: 140 }, { x: 280, y: 140 }], connectFrom: r1.id, connectTo: r2.id, connectFromEdge: 'right', connectToEdge: 'left' };
      const l2 = { id: genId(), tool: 'arrow' as WbTool, color: '#b06ef5', size: 2, points: [{ x: 400, y: 140 }, { x: 480, y: 140 }], connectFrom: r2.id, connectTo: c1.id, connectFromEdge: 'right', connectToEdge: 'left' };

      newStrokes = [r1, t1, r2, t2, c1, t3, l1, l2];
    } else if (type === 'database') {
      const r1 = { id: genId(), tool: 'rect' as WbTool, color: '#f43f5e', size: 2, points: [{ x: 100, y: 80 }, { x: 220, y: 200 }] };
      const t1 = { id: genId(), tool: 'text' as WbTool, color: '#f43f5e', size: 2, points: [{ x: 120, y: 110 }], text: 'users_table', parentId: r1.id };
      const t2 = { id: genId(), tool: 'text' as WbTool, color: '#94a3b8', size: 2, points: [{ x: 120, y: 135 }], text: 'id (PK)', parentId: r1.id };
      const t3 = { id: genId(), tool: 'text' as WbTool, color: '#94a3b8', size: 2, points: [{ x: 120, y: 160 }], text: 'email (str)', parentId: r1.id };

      const r2 = { id: genId(), tool: 'rect' as WbTool, color: '#0ea5e9', size: 2, points: [{ x: 300, y: 80 }, { x: 420, y: 200 }] };
      const t4 = { id: genId(), tool: 'text' as WbTool, color: '#0ea5e9', size: 2, points: [{ x: 320, y: 110 }], text: 'orders_table', parentId: r2.id };
      const t5 = { id: genId(), tool: 'text' as WbTool, color: '#94a3b8', size: 2, points: [{ x: 320, y: 135 }], text: 'id (PK)', parentId: r2.id };
      const t6 = { id: genId(), tool: 'text' as WbTool, color: '#94a3b8', size: 2, points: [{ x: 320, y: 160 }], text: 'user_id (FK)', parentId: r2.id };

      const l1 = { id: genId(), tool: 'arrow' as WbTool, color: '#8b5cf6', size: 2, points: [{ x: 220, y: 140 }, { x: 300, y: 140 }], connectFrom: r1.id, connectTo: r2.id, connectFromEdge: 'right', connectToEdge: 'left' };

      newStrokes = [r1, t1, t2, t3, r2, t4, t5, t6, l1];
    } else if (type === 'rest') {
      const r1 = { id: genId(), tool: 'rect' as WbTool, color: '#10b981', size: 2, points: [{ x: 80, y: 110 }, { x: 190, y: 170 }] };
      const t1 = { id: genId(), tool: 'text' as WbTool, color: '#10b981', size: 2, points: [{ x: 95, y: 140 }], text: 'GET /users', parentId: r1.id };

      const r2 = { id: genId(), tool: 'rect' as WbTool, color: '#eab308', size: 2, points: [{ x: 320, y: 110 }, { x: 430, y: 170 }] };
      const t2 = { id: genId(), tool: 'text' as WbTool, color: '#eab308', size: 2, points: [{ x: 330, y: 140 }], text: 'Controller', parentId: r2.id };

      const l1 = { id: genId(), tool: 'arrow' as WbTool, color: '#6366f1', size: 2, points: [{ x: 190, y: 130 }, { x: 320, y: 130 }], connectFrom: r1.id, connectTo: r2.id, connectFromEdge: 'right', connectToEdge: 'left' };
      const l2 = { id: genId(), tool: 'arrow' as WbTool, color: '#ec4899', size: 2, points: [{ x: 320, y: 150 }, { x: 190, y: 150 }], connectFrom: r2.id, connectTo: r1.id, connectFromEdge: 'left', connectToEdge: 'right' };

      newStrokes = [r1, t1, r2, t2, l1, l2];
    }

    if (newStrokes.length > 0) {
      setStrokes(prev => [...prev, ...newStrokes]);
      strokesRef.current = [...strokesRef.current, ...newStrokes];
      setRedoStack([]);
      if (!isDemo) {
        sendMessage('tdash:whiteboard:update', { slug: teamSlug, type: 'strokes', data: [...strokesRef.current, ...newStrokes] });
      }
    }
  }

  useEffect(() => {
    if (strokes.length > 0 && onStrokeAdded) {
      onStrokeAdded();
    }
  }, [strokes.length, onStrokeAdded]);
  const [redoStack, setRedoStack] = useState<StrokeItem[]>([]);
  const [drawing, setDrawing] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const [activeText, setActiveText] = useState<{ x: number; y: number; text: string; index?: number } | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const textInputRef = useRef<HTMLTextAreaElement>(null);
  const currentStroke = useRef<StrokeItem | null>(null);
  const panOffset = useRef({ x: 0, y: 0 });
  const panStart = useRef<{ mx: number; my: number; ox: number; oy: number } | null>(null);
  const spaceHeld = useRef(false);
  const dragInfo = useRef<{ type: 'move' | 'resize'; index: number; pointIndex?: number; startX: number; startY: number; origPoints: { x: number; y: number }[] } | null>(null);
  const cursors = useRef([
    { ...COLLAB_CURSORS[0], x: 62, y: 58 },
    { ...COLLAB_CURSORS[1], x: 28, y: 72 },
  ]);
  const [, forceUpdate] = useState(0);

  useEffect(() => { strokesRef.current = strokes; }, [strokes]);

  const scale = zoom / 100;
  const nextId = useRef(1);
  function genId() { return 's' + (nextId.current++); }

  function findShapeAt(x: number, y: number) {
    for (let i = strokesRef.current.length - 1; i >= 0; i--) {
      const s = strokesRef.current[i];
      if (s.tool === 'rect' || s.tool === 'circle') {
        const p0 = s.points[0], p1 = s.points[s.points.length - 1];
        const minX = Math.min(p0.x, p1.x), maxX = Math.max(p0.x, p1.x);
        const minY = Math.min(p0.y, p1.y), maxY = Math.max(p0.y, p1.y);
        if (x >= minX && x <= maxX && y >= minY && y <= maxY) return { index: i, id: s.id, p0, p1 };
      }
    }
    return null;
  }

  function getNearestEdge(x: number, y: number, p0: { x: number; y: number }, p1: { x: number; y: number }) {
    const cx = (p0.x + p1.x) / 2, cy = (p0.y + p1.y) / 2;
    const dx = x - cx, dy = y - cy;
    if (Math.abs(dx) > Math.abs(dy)) {
      return dx > 0 ? 'right' : 'left';
    } else {
      return dy > 0 ? 'bottom' : 'top';
    }
  }

  function getEdgePoint(edge: string, p0: { x: number; y: number }, p1: { x: number; y: number }) {
    const minX = Math.min(p0.x, p1.x), maxX = Math.max(p0.x, p1.x);
    const minY = Math.min(p0.y, p1.y), maxY = Math.max(p0.y, p1.y);
    const cx = (minX + maxX) / 2, cy = (minY + maxY) / 2;
    switch (edge) {
      case 'left': return { x: minX, y: cy };
      case 'right': return { x: maxX, y: cy };
      case 'top': return { x: cx, y: minY };
      case 'bottom': return { x: cx, y: maxY };
      default: return { x: cx, y: cy };
    }
  }

  function updateConnections(strokeList: StrokeItem[], movedId: string, dx: number, dy: number) {
    const moved = strokeList.find(s => s.id === movedId);
    if (!moved) return strokeList;
    return strokeList.map(s => {
      if (s.tool === 'arrow' || s.tool === 'line') {
        const pts = [...s.points];
        if (s.connectFrom === movedId && pts[0]) {
          const target = strokeList.find(t => t.id === movedId);
          if (target && (target.tool === 'rect' || target.tool === 'circle')) {
            const edgePt = getEdgePoint(s.connectFromEdge || 'center', target.points[0], target.points[target.points.length - 1]);
            pts[0] = edgePt;
          } else {
            pts[0] = { x: pts[0].x + dx, y: pts[0].y + dy };
          }
        }
        if (s.connectTo === movedId && pts[pts.length - 1]) {
          const target = strokeList.find(t => t.id === movedId);
          if (target && (target.tool === 'rect' || target.tool === 'circle')) {
            const edgePt = getEdgePoint(s.connectToEdge || 'center', target.points[0], target.points[target.points.length - 1]);
            pts[pts.length - 1] = edgePt;
          } else {
            pts[pts.length - 1] = { x: pts[pts.length - 1].x + dx, y: pts[pts.length - 1].y + dy };
          }
        }
        if (pts[0] !== s.points[0] || pts[pts.length - 1] !== s.points[s.points.length - 1]) {
          return { ...s, points: pts };
        }
      }
      if (s.parentId === movedId) {
        return { ...s, points: s.points.map(p => ({ x: p.x + dx, y: p.y + dy })) };
      }
      return s;
    });
  }

  function updateConnectionsResize(strokeList: StrokeItem[], resizedId: string) {
    return strokeList.map(s => {
      if (s.tool === 'arrow' || s.tool === 'line') {
        const pts = [...s.points];
        if (s.connectFrom === resizedId) {
          const target = strokeList.find(t => t.id === resizedId);
          if (target && (target.tool === 'rect' || target.tool === 'circle')) {
            const edgePt = getEdgePoint(s.connectFromEdge || 'center', target.points[0], target.points[target.points.length - 1]);
            pts[0] = edgePt;
          }
        }
        if (s.connectTo === resizedId) {
          const target = strokeList.find(t => t.id === resizedId);
          if (target && (target.tool === 'rect' || target.tool === 'circle')) {
            const edgePt = getEdgePoint(s.connectToEdge || 'center', target.points[0], target.points[target.points.length - 1]);
            pts[pts.length - 1] = edgePt;
          }
        }
        return { ...s, points: pts };
      }
      return s;
    });
  }

  function findTextAt(x: number, y: number) {
    for (let i = strokesRef.current.length - 1; i >= 0; i--) {
      const s = strokesRef.current[i];
      if (s.tool === 'text' && s.points[0]) {
        const p = s.points[0];
        const dist = Math.sqrt((x - p.x) ** 2 + (y - p.y) ** 2);
        if (dist < 40) return i;
      }
    }
    return -1;
  }

  function hitTest(x: number, y: number) {
    for (let i = strokesRef.current.length - 1; i >= 0; i--) {
      const s = strokesRef.current[i];
      if (!s.points[0]) continue;
      const p0 = s.points[0];
      const last = s.points[s.points.length - 1];
      if (s.tool === 'text' && s.points[0]) {
        const p = s.points[0];
        const dist = Math.sqrt((x - p.x) ** 2 + (y - p.y) ** 2);
        if (dist < 40) return { index: i, type: 'move' as const };
      }
      if (s.tool === 'pen') {
        for (const p of s.points) {
          if (Math.sqrt((x - p.x) ** 2 + (y - p.y) ** 2) < 10) return { index: i, type: 'move' as const };
        }
      }
      if (s.tool === 'line' || s.tool === 'arrow') {
        const dist = pointToLineDistance(x, y, p0.x, p0.y, last.x, last.y);
        if (dist < 10) return { index: i, type: 'move' as const };
        const d0 = Math.sqrt((x - p0.x) ** 2 + (y - p0.y) ** 2);
        const d1 = Math.sqrt((x - last.x) ** 2 + (y - last.y) ** 2);
        if (d0 < 12) return { index: i, type: 'resize' as const, pointIndex: 0 };
        if (d1 < 12) return { index: i, type: 'resize' as const, pointIndex: s.points.length - 1 };
      }
      if (s.tool === 'rect' || s.tool === 'circle') {
        const minX = Math.min(p0.x, last.x), maxX = Math.max(p0.x, last.x);
        const minY = Math.min(p0.y, last.y), maxY = Math.max(p0.y, last.y);
        if (x >= minX && x <= maxX && y >= minY && y <= maxY) return { index: i, type: 'move' as const };
        const corners = [
          { x: p0.x, y: p0.y, idx: 0 },
          { x: last.x, y: p0.y, idx: 1 },
          { x: last.x, y: last.y, idx: s.points.length - 1 },
          { x: p0.x, y: last.y, idx: s.points.length - 2 || 1 },
        ];
        for (const c of corners) {
          if (Math.sqrt((x - c.x) ** 2 + (y - c.y) ** 2) < 12) {
            return { index: i, type: 'resize' as const, pointIndex: c.idx };
          }
        }
      }
    }
    return null;
  }

  function pointToLineDistance(px: number, py: number, x1: number, y1: number, x2: number, y2: number) {
    const A = px - x1, B = py - y1, C = x2 - x1, D = y2 - y1;
    const dot = A * C + B * D;
    const lenSq = C * C + D * D;
    let param = -1;
    if (lenSq !== 0) param = dot / lenSq;
    let xx, yy;
    if (param < 0) { xx = x1; yy = y1; }
    else if (param > 1) { xx = x2; yy = y2; }
    else { xx = x1 + param * C; yy = y1 + param * D; }
    const dx = px - xx, dy = py - yy;
    return Math.sqrt(dx * dx + dy * dy);
  }

  function commitText(text: string) {
    if (!activeText) return;
    if (!text.trim()) {
      if (activeText.index !== undefined) {
        setStrokes(prev => {
          const next = prev.filter((_, i) => i !== activeText.index);
          strokesRef.current = next;
          if (!isDemo) {
            sendMessage('tdash:whiteboard:update', { slug: teamSlug, type: 'strokes', data: next });
          }
          return next;
        });
      }
      setActiveText(null);
      return;
    }
    if (activeText.index !== undefined) {
      setStrokes(prev => {
        const next = prev.map((s, i) => i === activeText.index ? { ...s, text } : s);
        strokesRef.current = next;
        if (!isDemo) {
          sendMessage('tdash:whiteboard:update', { slug: teamSlug, type: 'strokes', data: next });
        }
        return next;
      });
    } else {
      const parent = findShapeAt(activeText.x, activeText.y);
      setStrokes(prev => {
        const newText: StrokeItem = {
          id: genId(),
          tool: 'text',
          color,
          size: 2,
          points: [{ x: activeText.x, y: activeText.y }],
          text,
          parentId: parent ? parent.id : undefined,
        };
        const next = [...prev, newText];
        strokesRef.current = next;
        if (!isDemo) {
          sendMessage('tdash:whiteboard:update', { slug: teamSlug, type: 'strokes', data: next });
        }
        return next;
      });
    }
    setRedoStack([]);
    setActiveText(null);
  }

  const seedDemoScene = useCallback((): StrokeItem[] => {
    const r1 = { id: genId(), tool: 'rect' as WbTool, color: '#4d9cf8', size: 2, points: [{ x: 80, y: 70 }, { x: 290, y: 180 }] };
    const t1 = { id: genId(), tool: 'text' as WbTool, color: '#4d9cf8', size: 2, points: [{ x: 96, y: 104 }], text: 'Launch plan', parentId: r1.id };
    const t2 = { id: genId(), tool: 'text' as WbTool, color: '#e8e8f0', size: 2, points: [{ x: 98, y: 132 }], text: 'Frontend polish + QA dry run', parentId: r1.id };
    const r2 = { id: genId(), tool: 'rect' as WbTool, color: '#3ecf8e', size: 2, points: [{ x: 360, y: 92 }, { x: 560, y: 215 }] };
    const t3 = { id: genId(), tool: 'text' as WbTool, color: '#3ecf8e', size: 2, points: [{ x: 378, y: 126 }], text: 'Retention ideas', parentId: r2.id };
    const t4 = { id: genId(), tool: 'text' as WbTool, color: '#e8e8f0', size: 2, points: [{ x: 378, y: 154 }], text: 'Luma tips + team nudges', parentId: r2.id };
    const c1 = { id: genId(), tool: 'circle' as WbTool, color: '#f5a623', size: 2, points: [{ x: 640, y: 92 }, { x: 820, y: 215 }] };
    const t5 = { id: genId(), tool: 'text' as WbTool, color: '#f5a623', size: 2, points: [{ x: 690, y: 145 }], text: 'Review loop', parentId: c1.id };
    const l1 = { id: genId(), tool: 'arrow' as WbTool, color: '#b06ef5', size: 2, points: [{ x: 290, y: 126 }, { x: 360, y: 126 }], connectFrom: r1.id, connectTo: r2.id, connectFromEdge: 'right', connectToEdge: 'left' };
    const l2 = { id: genId(), tool: 'arrow' as WbTool, color: '#b06ef5', size: 2, points: [{ x: 560, y: 150 }, { x: 640, y: 150 }], connectFrom: r2.id, connectTo: c1.id, connectFromEdge: 'right', connectToEdge: 'left' };
    return [r1, t1, t2, r2, t3, t4, c1, t5, l1, l2];
  }, []);

  // Animate fake collab cursors
  useEffect(() => {
    const tids: ReturnType<typeof setTimeout>[] = [];
    const move = (i: number) => {
      cursors.current[i].x = Math.max(5, Math.min(90, cursors.current[i].x + (Math.random() - 0.5) * 4));
      cursors.current[i].y = Math.max(10, Math.min(85, cursors.current[i].y + (Math.random() - 0.5) * 3));
      forceUpdate(n => n + 1);
      tids[i] = setTimeout(() => move(i), 700 + Math.random() * 900);
    };
    COLLAB_CURSORS.forEach((_, i) => move(i));
    return () => tids.forEach(t => clearTimeout(t));
  }, []);

  useEffect(() => {
    if (!isDemo) return;
    setStrokes(seedDemoScene());
    setRedoStack([]);
  }, [isDemo, seedDemoScene]);

  useEffect(() => {
    if (activeText && textInputRef.current) {
      textInputRef.current.focus();
      textInputRef.current.select();
    }
  }, [activeText]);

  const drawStroke = useCallback((ctx: CanvasRenderingContext2D, s: StrokeItem | null | undefined) => {
    if (!s || !Array.isArray(s.points) || s.points.length === 0) return;
    ctx.strokeStyle = s.tool === 'eraser' ? '#000000' : s.color;
    ctx.fillStyle = s.color;
    ctx.lineWidth = s.size;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.globalCompositeOperation = s.tool === 'eraser' ? 'destination-out' : 'source-over';
    const p0 = s.points[0];
    const last = s.points[s.points.length - 1];
    if (s.tool === 'pen' || s.tool === 'eraser') {
      ctx.beginPath(); ctx.moveTo(p0.x, p0.y);
      s.points.slice(1).forEach(p => ctx.lineTo(p.x, p.y));
      ctx.stroke();
    } else if (s.tool === 'line') {
      ctx.beginPath(); ctx.moveTo(p0.x, p0.y); ctx.lineTo(last.x, last.y); ctx.stroke();
    } else if (s.tool === 'rect') {
      ctx.strokeRect(p0.x, p0.y, last.x - p0.x, last.y - p0.y);
    } else if (s.tool === 'circle') {
      const rx = Math.abs(last.x - p0.x) / 2; const ry = Math.abs(last.y - p0.y) / 2;
      ctx.beginPath(); ctx.ellipse((p0.x + last.x) / 2, (p0.y + last.y) / 2, rx || 1, ry || 1, 0, 0, Math.PI * 2); ctx.stroke();
    } else if (s.tool === 'text' && s.text) {
      ctx.font = `500 14px sans-serif`;
      const lines = s.text.split('\n');
      lines.forEach((line, i) => {
        ctx.fillText(line, p0.x, p0.y + i * 18);
      });
    } else if (s.tool === 'arrow') {
      const fromX = p0.x, fromY = p0.y;
      const toX = last.x, toY = last.y;
      const headLength = Math.max(10, s.size * 3);
      const dx = toX - fromX;
      const dy = toY - fromY;
      const angle = Math.atan2(dy, dx);
      ctx.beginPath();
      ctx.moveTo(fromX, fromY);
      ctx.lineTo(toX, toY);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(toX, toY);
      ctx.lineTo(toX - headLength * Math.cos(angle - Math.PI / 6), toY - headLength * Math.sin(angle - Math.PI / 6));
      ctx.lineTo(toX - headLength * Math.cos(angle + Math.PI / 6), toY - headLength * Math.sin(angle + Math.PI / 6));
      ctx.closePath();
      ctx.fill();
    }
    ctx.globalCompositeOperation = 'source-over';
  }, []);

  function drawHandles(ctx: CanvasRenderingContext2D, s: StrokeItem) {
    if (!s || s.points.length === 0) return;
    const p0 = s.points[0];
    const last = s.points[s.points.length - 1];
    ctx.fillStyle = '#4d9cf8';
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 1;
    const drawHandle = (x: number, y: number) => {
      ctx.beginPath();
      ctx.arc(x, y, 4, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    };
    if (s.tool === 'line' || s.tool === 'arrow') {
      drawHandle(p0.x, p0.y);
      drawHandle(last.x, last.y);
    } else if (s.tool === 'rect' || s.tool === 'circle') {
      const corners = [
        { x: p0.x, y: p0.y },
        { x: last.x, y: p0.y },
        { x: last.x, y: last.y },
        { x: p0.x, y: last.y },
      ];
      corners.forEach(c => drawHandle(c.x, c.y));
    } else if (s.tool === 'text') {
      drawHandle(p0.x, p0.y);
    }
  }

  const redraw = useCallback(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext('2d'); if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);
    // Dot grid
    const spacing = 22 * scale;
    const ox = (panOffset.current.x % spacing + spacing) % spacing;
    const oy = (panOffset.current.y % spacing + spacing) % spacing;
    ctx.fillStyle = 'rgba(120,120,140,.25)';
    for (let gx = ox; gx < canvas.width / dpr; gx += spacing)
      for (let gy = oy; gy < canvas.height / dpr; gy += spacing) {
        ctx.beginPath(); ctx.arc(gx, gy, 1, 0, Math.PI * 2); ctx.fill();
      }
    ctx.save();
    ctx.translate(panOffset.current.x, panOffset.current.y);
    ctx.scale(scale, scale);
    strokesRef.current.forEach((s, i) => {
      drawStroke(ctx, s);
      if (selectedIndex === i && tool === 'select') {
        ctx.strokeStyle = '#4d9cf8';
        ctx.lineWidth = 1;
        ctx.setLineDash([4, 4]);
        if (s.tool === 'rect' || s.tool === 'circle') {
          const minX = Math.min(s.points[0].x, s.points[s.points.length - 1].x);
          const maxX = Math.max(s.points[0].x, s.points[s.points.length - 1].x);
          const minY = Math.min(s.points[0].y, s.points[s.points.length - 1].y);
          const maxY = Math.max(s.points[0].y, s.points[s.points.length - 1].y);
          ctx.strokeRect(minX - 4, minY - 4, maxX - minX + 8, maxY - minY + 8);
        } else if (s.tool === 'line' || s.tool === 'arrow') {
          const p0 = s.points[0];
          const last = s.points[s.points.length - 1];
          ctx.beginPath();
          ctx.moveTo(p0.x, p0.y);
          ctx.lineTo(last.x, last.y);
          ctx.stroke();
        } else if (s.tool === 'text') {
          const p = s.points[0];
          ctx.strokeRect(p.x - 4, p.y - 18, 80, 22);
        }
        ctx.setLineDash([]);
        drawHandles(ctx, s);
      }
    });
    if (currentStroke.current) drawStroke(ctx, currentStroke.current);
    ctx.restore();
  }, [scale, drawStroke, selectedIndex, tool]);

  useEffect(() => {
    if (isDemo) return;
    return on('tdash:whiteboard:updated', (event) => {
      const payload = (event.data || {}) as { slug?: string; type?: 'strokes' | 'notes' | 'clear'; data?: any };
      if (payload.slug !== teamSlug) return;
      if (payload.type === 'strokes') {
        setStrokes(payload.data || []);
        strokesRef.current = payload.data || [];
        redraw();
      } else if (payload.type === 'notes') {
        setNotes(payload.data || []);
      } else if (payload.type === 'clear') {
        setStrokes([]);
        strokesRef.current = [];
        setNotes([]);
        redraw();
      }
    });
  }, [on, isDemo, teamSlug, redraw]);

  useEffect(() => {
    if (isDemo) return;
    return on('tdash:whiteboard:cursor:updated', (event) => {
      const payload = (event.data || {}) as { slug?: string; userId?: string; cursor?: { x: number; y: number } };
      if (payload.slug !== teamSlug || !payload.userId || !payload.cursor) return;
      const member = members.find(m => m.userId === payload.userId) || { name: 'Teammate', role: 'dev' };
      setLiveCursors(prev => ({
        ...prev,
        [payload.userId!]: {
          x: payload.cursor!.x,
          y: payload.cursor!.y,
          name: member.name,
          color: (member as any).color || '#8b5cf6',
        },
      }));
    });
  }, [on, isDemo, teamSlug, members]);

  // Resize canvas on mount and resize
  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current; const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = wrap.clientWidth * dpr;
    canvas.height = wrap.clientHeight * dpr;
    canvas.style.width = wrap.clientWidth + 'px';
    canvas.style.height = wrap.clientHeight + 'px';
    const ctx = canvas.getContext('2d'); if (ctx) ctx.scale(dpr, dpr);
    redraw();
  }, [redraw]);

  useEffect(() => { initCanvas(); }, [initCanvas]);
  useEffect(() => { redraw(); }, [redraw]);
  useEffect(() => {
    window.addEventListener('resize', initCanvas);
    return () => window.removeEventListener('resize', initCanvas);
  }, [initCanvas]);

  useEffect(() => {
    const kd = (e: KeyboardEvent) => {
      if (e.code === 'Space' && !['INPUT','TEXTAREA'].includes((e.target as HTMLElement).tagName)) {
        e.preventDefault(); spaceHeld.current = true; setIsPanning(true);
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') { e.preventDefault(); undo(); }
    };
    const ku = (e: KeyboardEvent) => { if (e.code === 'Space') { spaceHeld.current = false; setIsPanning(false); } };
    window.addEventListener('keydown', kd);
    window.addEventListener('keyup', ku);
    return () => { window.removeEventListener('keydown', kd); window.removeEventListener('keyup', ku); };
  }, []);

  function toCanvas(clientX: number, clientY: number) {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return { x: (clientX - rect.left - panOffset.current.x) / scale, y: (clientY - rect.top - panOffset.current.y) / scale };
  }

  function onDown(e: React.MouseEvent) {
    if (e.button === 1) { e.preventDefault(); return; }
    if (spaceHeld.current || e.button === 2) {
      panStart.current = { mx: e.clientX, my: e.clientY, ox: panOffset.current.x, oy: panOffset.current.y };
      setIsPanning(true); return;
    }
    if (tool === 'select') {
      const pt = toCanvas(e.clientX, e.clientY);
      const hit = hitTest(pt.x, pt.y);
      if (hit) {
        setSelectedIndex(hit.index);
        dragInfo.current = {
          type: hit.type,
          index: hit.index,
          pointIndex: hit.pointIndex,
          startX: pt.x,
          startY: pt.y,
          origPoints: strokesRef.current[hit.index].points.map(p => ({ ...p })),
        };
      } else {
        setSelectedIndex(null);
        panStart.current = { mx: e.clientX, my: e.clientY, ox: panOffset.current.x, oy: panOffset.current.y };
        setIsPanning(true);
      }
      return;
    }
    if (tool === 'text') {
      const pt = toCanvas(e.clientX, e.clientY);
      if (activeText) { commitText(activeText.text); }
      const idx = findTextAt(pt.x, pt.y);
      if (idx >= 0) {
        setActiveText({ x: strokesRef.current[idx].points[0].x, y: strokesRef.current[idx].points[0].y, text: strokesRef.current[idx].text || '', index: idx });
      } else {
        setActiveText({ x: pt.x, y: pt.y, text: '', index: undefined });
      }
      return;
    }
    if (tool === 'sticky') {
      const pt = toCanvas(e.clientX, e.clientY);
      setNotes(prev => {
        const next = [...prev, { id: Date.now(), x: pt.x, y: pt.y, text: 'Click to edit...', color: ['#fef08a', '#86efac', '#93c5fd', '#f9a8d4'][Math.floor(Math.random() * 4)] }];
        if (!isDemo) {
          sendMessage('tdash:whiteboard:update', { slug: teamSlug, type: 'notes', data: next });
        }
        return next;
      });
      return;
    }
    currentStroke.current = { id: genId(), tool, color, size: tool === 'eraser' ? Math.max(18, brushSize * 8) : brushSize, points: [toCanvas(e.clientX, e.clientY)] };
    setDrawing(true);
  }

  function onMove(e: React.MouseEvent) {
    const pt = toCanvas(e.clientX, e.clientY);
    if (!isDemo) {
      const now = Date.now();
      if (now - lastCursorSent.current > 50) {
        lastCursorSent.current = now;
        sendMessage('tdash:whiteboard:cursor', { slug: teamSlug, cursor: { x: pt.x, y: pt.y } });
      }
    }

    if (dragInfo.current) {
      const d = dragInfo.current;
      const dx = pt.x - d.startX;
      const dy = pt.y - d.startY;
      setStrokes(prev => {
        let next = prev.map((s, i) => {
          if (i !== d.index) return s;
          const newPoints = s.points.map((p, pi) => {
            if (d.type === 'move') {
              return { x: p.x + dx, y: p.y + dy };
            } else {
              if (pi === d.pointIndex) return { x: pt.x, y: pt.y };
              return { x: p.x, y: p.y };
            }
          });
          return { ...s, points: newPoints };
        });
        if (d.type === 'move') {
          const movedId = prev[d.index].id;
          next = updateConnections(next, movedId, dx, dy);
        } else if (d.type === 'resize') {
          const resizedId = prev[d.index].id;
          next = updateConnectionsResize(next, resizedId);
        }
        strokesRef.current = next;
        return next;
      });
      dragInfo.current = { ...d, startX: pt.x, startY: pt.y };
      redraw();
      return;
    }
    if (panStart.current) {
      panOffset.current = { x: panStart.current.ox + e.clientX - panStart.current.mx, y: panStart.current.oy + e.clientY - panStart.current.my };
      redraw(); return;
    }
    if (!drawing || !currentStroke.current) return;
    if (tool === 'pen' || tool === 'eraser') currentStroke.current.points.push(pt);
    else currentStroke.current.points = [currentStroke.current.points[0], pt];
    redraw();
  }

  function onUp() {
    if (dragInfo.current) {
      dragInfo.current = null;
      if (!isDemo) {
        sendMessage('tdash:whiteboard:update', { slug: teamSlug, type: 'strokes', data: strokes });
      }
      return;
    }
    if (panStart.current) { panStart.current = null; setIsPanning(false); return; }
    if (!drawing || !currentStroke.current) return;
    const finalized = currentStroke.current;
    if (finalized.points.length > 0) {
      const newStroke = { ...finalized, id: genId() };
      if (newStroke.tool === 'arrow' || newStroke.tool === 'line') {
        const p0 = newStroke.points[0];
        const last = newStroke.points[newStroke.points.length - 1];
        const fromShape = findShapeAt(p0.x, p0.y);
        const toShape = findShapeAt(last.x, last.y);
        if (fromShape) {
          newStroke.connectFrom = fromShape.id;
          newStroke.connectFromEdge = getNearestEdge(p0.x, p0.y, fromShape.p0, fromShape.p1);
          newStroke.points[0] = getEdgePoint(newStroke.connectFromEdge, fromShape.p0, fromShape.p1);
        }
        if (toShape) {
          newStroke.connectTo = toShape.id;
          newStroke.connectToEdge = getNearestEdge(last.x, last.y, toShape.p0, toShape.p1);
          newStroke.points[newStroke.points.length - 1] = getEdgePoint(newStroke.connectToEdge, toShape.p0, toShape.p1);
        }
      }
      setStrokes(prev => {
        const next = [...prev, newStroke];
        strokesRef.current = next;
        if (!isDemo) {
          sendMessage('tdash:whiteboard:update', { slug: teamSlug, type: 'strokes', data: next });
        }
        return next;
      });
      setRedoStack([]);
    }
    currentStroke.current = null; setDrawing(false);
  }

  function onDoubleClick(e: React.MouseEvent) {
    const pt = toCanvas(e.clientX, e.clientY);
    const idx = findTextAt(pt.x, pt.y);
    if (idx >= 0) {
      if (activeText) { commitText(activeText.text); }
      setActiveText({ x: strokesRef.current[idx].points[0].x, y: strokesRef.current[idx].points[0].y, text: strokesRef.current[idx].text || '', index: idx });
    }
  }

  function exportBoard() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = `whiteboard-${Date.now()}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  }

  function onWheel(e: React.WheelEvent) {
    e.preventDefault();
    setZoom(z => Math.max(25, Math.min(300, z + (e.deltaY < 0 ? 10 : -10))));
  }

  function undo() {
    setStrokes(prev => {
      if (!prev.length) { strokesRef.current = prev; return prev; }
      setRedoStack(r => [...r, prev[prev.length - 1]]);
      const next = prev.slice(0, -1);
      strokesRef.current = next;
      if (!isDemo) {
        sendMessage('tdash:whiteboard:update', { slug: teamSlug, type: 'strokes', data: next });
      }
      return next;
    });
  }
  function redo() {
    setRedoStack(prev => {
      if (!prev.length) return prev;
      const stroke = prev[prev.length - 1];
      setStrokes(s => {
        const next = [...s, stroke];
        strokesRef.current = next;
        if (!isDemo) {
          sendMessage('tdash:whiteboard:update', { slug: teamSlug, type: 'strokes', data: next });
        }
        return next;
      });
      return prev.slice(0, -1);
    });
  }

  const TOOLS: { id: WbTool; title: string; icon: React.ReactNode }[] = [
    { id: 'select', title: 'Select', icon: <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><path d="M2.5 2.5l3.5 9 1.8-3.2 3.2-1.8-8.5-4z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/></svg> },
    { id: 'pen', title: 'Pen', icon: <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><path d="M11 2l3 3L5 14H2v-3L11 2z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/></svg> },
    { id: 'line', title: 'Line', icon: <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><path d="M2 14L14 2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg> },
    { id: 'rect', title: 'Rectangle', icon: <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><rect x="2" y="3" width="12" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.4"/></svg> },
    { id: 'circle', title: 'Circle', icon: <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.4"/></svg> },
    { id: 'arrow', title: 'Arrow', icon: <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><path d="M2 14L11 5M11 5H7M11 5v4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg> },
    { id: 'text', title: 'Text', icon: <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><path d="M3 4h10M8 4v8M5 12h6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg> },
    { id: 'sticky', title: 'Sticky note', icon: <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><rect x="2" y="2" width="12" height="12" rx="1.6" stroke="currentColor" strokeWidth="1.2"/><path d="M10 10h2v2h-2z" fill="currentColor" opacity=".5"/></svg> },
    { id: 'eraser', title: 'Eraser', icon: <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><path d="M3 13h10M6 9l4-4-3-3-6 6 2 2 3-1z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/></svg> },
  ];

  return (
    <div ref={wrapRef} className="wb-wrap" onWheel={onWheel} style={{ flex: 1, position: 'relative', overflow: 'hidden', background: 'var(--bg)' }}>
      {/* Toolbar */}
      <div className="wb-toolbar">
        {TOOLS.map(t => (
          <button key={t.id} className={`wb-tool-btn canvas-tool${tool === t.id ? ' active' : ''}`} title="" onClick={() => setTool(t.id)}>{t.icon}<span className="tool-tooltip">{t.title}</span></button>
        ))}
        <div className="wb-divider wb-sep" />
        {WB_COLORS.map(c => (
          <div key={c} className={`wb-color-swatch color-dot${color === c ? ' active' : ''}`} style={{ background: c }} onClick={() => setColor(c)} title={c} />
        ))}
        <div className="wb-divider wb-sep" />
        <div className="wb-zoom-ctrl">
          <span className="wb-zoom-pct" style={{ minWidth: 44 }}>Size</span>
          <input type="range" min={1} max={14} value={brushSize} onChange={(e) => setBrushSize(Number(e.target.value))} style={{ width: 80 }} />
          <span className="wb-zoom-pct" style={{ minWidth: 26 }}>{brushSize}</span>
        </div>
        <div className="wb-divider wb-sep" />
        <button className="wb-tool-btn" title="Undo (Ctrl+Z)" onClick={undo}>
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M3 8a5 5 0 1 0 1.5-3.5L2 2M2 2v4h4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </button>
        <button className="wb-tool-btn" title="Redo" onClick={redo}>
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M13 8a5 5 0 1 1-1.5-3.5L14 2M14 2v4h-4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </button>
        <div className="wb-divider wb-sep" />
        <div className="wb-zoom-ctrl">
          <button className="wb-zoom-btn" onClick={() => setZoom(z => Math.max(25, z - 10))}>−</button>
          <span className="wb-zoom-pct wb-zoom-display">{zoom}%</span>
          <button className="wb-zoom-btn" onClick={() => setZoom(z => Math.min(300, z + 10))}>+</button>
        </div>
        <div className="wb-divider wb-sep" />
        <button className="wb-tool-btn" title="Clear canvas" onClick={() => {
          setStrokes([]);
          strokesRef.current = [];
          setRedoStack([]);
          setNotes([]);
          if (!isDemo) {
            sendMessage('tdash:whiteboard:update', { slug: teamSlug, type: 'clear' });
          }
        }}>
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M2 4h12M5 4V2h6v2M6 8v4M10 8v4M3 4l1 10h8l1-10" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </button>
        <button className="wb-tool-btn" title="Load demo scene" onClick={() => {
          const s = seedDemoScene();
          setStrokes(s);
          strokesRef.current = s;
          setRedoStack([]);
          if (!isDemo) {
            sendMessage('tdash:whiteboard:update', { slug: teamSlug, type: 'strokes', data: s });
          }
        }}>
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M8 2v3M8 11v3M2 8h3M11 8h3M3.8 3.8l2.1 2.1M10.1 10.1l2.1 2.1M12.2 3.8l-2.1 2.1M5.9 10.1l-2.1 2.1" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>
        </button>
        <button className="wb-tool-btn" title="Export PNG" onClick={exportBoard}>
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M8 2v7M5.5 6.5L8 9l2.5-2.5M3 11.5h10" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </button>
        <div className="wb-divider wb-sep" />
        <div className="relative">
          <button 
            className={`wb-tool-btn canvas-tool${showTemplates ? ' active' : ''}`} 
            title="Load AI Flow Templates" 
            onClick={() => setShowTemplates(prev => !prev)}
            style={{ position: 'relative' }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
            </svg>
            <span className="tool-tooltip">Templates</span>
          </button>
          
          {showTemplates && (
            <div className="absolute bottom-10 left-0 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl p-3 shadow-xl flex flex-col gap-2 z-50 min-w-[170px]" style={{ pointerEvents: 'auto' }}>
              <div className="text-[9px] font-black uppercase text-zinc-400 dark:text-zinc-500 tracking-wider pb-1.5 border-b border-zinc-150 dark:border-zinc-800/80">
                AI Architect Presets
              </div>
              <button 
                onClick={() => { applyTemplate('architecture'); setShowTemplates(false); }}
                className="text-left text-xs font-bold py-1.5 px-2 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 rounded-lg text-zinc-700 dark:text-zinc-300 transition-colors"
              >
                🖥️ System Architecture
              </button>
              <button 
                onClick={() => { applyTemplate('database'); setShowTemplates(false); }}
                className="text-left text-xs font-bold py-1.5 px-2 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 rounded-lg text-zinc-700 dark:text-zinc-300 transition-colors"
              >
                🗄️ Database Schema
              </button>
              <button 
                onClick={() => { applyTemplate('rest'); setShowTemplates(false); }}
                className="text-left text-xs font-bold py-1.5 px-2 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 rounded-lg text-zinc-700 dark:text-zinc-300 transition-colors"
              >
                🌐 REST API Flow
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Drawing canvas */}
      <canvas
        ref={canvasRef}
        className={`wb-canvas${isPanning ? ' panning' : ''}`}
        onMouseDown={onDown}
        onMouseMove={onMove}
        onMouseUp={onUp}
        onMouseLeave={onUp}
        onDoubleClick={onDoubleClick}
        onContextMenu={(e) => e.preventDefault()}
        style={{ position: 'absolute', inset: 0 }}
      />

      <div style={{ position: 'absolute', inset: '0 0 32px 0', pointerEvents: 'none', overflow: 'hidden' }}>
        {notes.map(note => (
          <div key={note.id} className="wb-sticky" style={{ left: note.x * scale + panOffset.current.x, top: note.y * scale + panOffset.current.y, background: note.color, pointerEvents: 'auto' }}>
            <div contentEditable suppressContentEditableWarning onBlur={(e) => setNotes(prev => {
              const next = prev.map(n => n.id === note.id ? { ...n, text: e.currentTarget.textContent || '' } : n);
              if (!isDemo) {
                sendMessage('tdash:whiteboard:update', { slug: teamSlug, type: 'notes', data: next });
              }
              return next;
            })}>{note.text}</div>
          </div>
        ))}
      </div>

      {activeText && (
        <textarea
          ref={textInputRef}
          value={activeText.text}
          placeholder="Type here..."
          spellCheck={false}
          autoComplete="off"
          autoFocus
          rows={1}
          onMouseDown={(e) => e.stopPropagation()}
          onChange={(e) => setActiveText({ ...activeText, text: e.target.value })}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); commitText(activeText.text); }
            if (e.key === 'Enter' && e.shiftKey) { e.preventDefault(); setActiveText({ ...activeText, text: activeText.text + '\n' }); }
            if (e.key === 'Escape') { setActiveText(null); }
          }}
          onBlur={() => commitText(activeText.text)}
          style={{
            position: 'absolute',
            left: activeText.x * scale + panOffset.current.x,
            top: activeText.y * scale + panOffset.current.y - 14,
            fontSize: 14,
            fontFamily: 'sans-serif',
            fontWeight: 500,
            color: activeText.index !== undefined ? (strokes[activeText.index]?.color || color) : color,
            background: 'rgba(255,255,255,0.95)',
            border: '2px solid #4d9cf8',
            borderRadius: 4,
            outline: 'none',
            padding: '4px 8px',
            pointerEvents: 'auto',
            zIndex: 100,
            minWidth: 120,
            minHeight: 20,
            resize: 'none',
            overflow: 'hidden',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          }}
        />
      )}

      {/* Collab cursors */}
      {isDemo ? (
        cursors.current.map((c, i) => (
          <div key={i} className="wb-collab-cursor" style={{ left: `${c.x}%`, top: `${c.y}%` }}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 2l10 4-5 2-2 5L2 2z" fill={c.color} stroke="rgba(0,0,0,.4)" strokeWidth=".8"/></svg>
            <div className="wb-collab-label" style={{ background: c.color, color: '#000', fontSize: 9, padding: '1px 5px', borderRadius: 4, marginTop: 1, display: 'inline-block', fontFamily: 'var(--mono)', fontWeight: 600 }}>{c.name}</div>
          </div>
        ))
      ) : (
        Object.entries(liveCursors).map(([userId, c]) => (
          <div key={userId} className="wb-collab-cursor" style={{ left: c.x * scale + panOffset.current.x, top: c.y * scale + panOffset.current.y, position: 'absolute', pointerEvents: 'none', transition: 'all 0.08s ease' }}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 2l10 4-5 2-2 5L2 2z" fill={c.color} stroke="rgba(0,0,0,.4)" strokeWidth=".8"/></svg>
            <div className="wb-collab-label" style={{ background: c.color, color: '#000', fontSize: 9, padding: '1px 5px', borderRadius: 4, marginTop: 1, display: 'inline-block', fontFamily: 'var(--mono)', fontWeight: 600 }}>{c.name}</div>
          </div>
        ))
      )}

      <div className="wb-statusbar">
        <span>
          <span className="wb-online-dot" style={{ background: (isDemo || Object.keys(liveCursors).length > 0) ? 'var(--green)' : 'var(--text3)', boxShadow: (isDemo || Object.keys(liveCursors).length > 0) ? '0 0 5px var(--green)' : 'none' }} />
          &nbsp;{isDemo ? '2' : Object.keys(liveCursors).length} teammate{Object.keys(liveCursors).length === 1 ? '' : 's'} drawing live
        </span>
        <span style={{ color: 'var(--text2)' }}>{tool.charAt(0).toUpperCase() + tool.slice(1)}</span>
        <span style={{ marginLeft: 'auto', opacity: .7 }}>Ctrl+Z undo · Scroll zoom · Middle-drag to pan · {zoom}%</span>
      </div>
    </div>
  );
}

// ─── MAIN ──────────────────────────────────────────────────────────────────
export default function DiscussionPage() {
  const { showToast, isReal, teamSlug, members, theme } = useTeamDashboard();
  const { on, sendMessage } = useWebSocket();
  const [tab, setTab] = useState<DiscTab>('chat');
  const [roadmapCols, setRoadmapCols] = useState<RoadmapCol[]>(ROADMAP_COLS);
  const [showAddItem, setShowAddItem] = useState(false);
  const [newItem, setNewItem] = useState({ title: '', column: 'backlog' as RoadmapColId, priority: 'medium' as 'high' | 'medium' | 'low' });
  const dragItem = useRef<{ fromCol: RoadmapColId; item: RoadmapItem } | null>(null);
  const [dragOverCol, setDragOverCol] = useState<RoadmapColId | null>(null);
  const [draggingCardId, setDraggingCardId] = useState<string | null>(null);
  const [dropFlashCol, setDropFlashCol] = useState<RoadmapColId | null>(null);
  const [lastDroppedCardId, setLastDroppedCardId] = useState<string | null>(null);
  const [chatMsgs, setChatMsgs] = useState<ChatMsg[]>(INIT_CHAT);
  const [activeThreadMsg, setActiveThreadMsg] = useState<ChatMsg | null>(null);
  const [threadReplies, setThreadReplies] = useState<Record<string, ChatMsg[]>>({});
  const [threadInput, setThreadInput] = useState('');
  const [chatReactions, setChatReactions] = useState<Record<string, Record<string, number>>>({});
  const [showEmojiPickerIndex, setShowEmojiPickerIndex] = useState<number | null>(null);
  const [chatInput, setChatInput] = useState('');
  const [chatImages, setChatImages] = useState<string[]>([]);
  const [aiMsgs, setAiMsgs] = useState<AiMsg[]>([{
    role: 'bot',
    text: <>Hey team! I'm watching your commits, tracking team patterns, and ready to help.<br /><span style={{ color: 'var(--text3)', fontSize: 11, fontFamily: 'var(--mono)' }}>Try: "merge PR" · "sprint risks" · "quality" · "auth history"</span></>,
  }]);
  const [aiInput, setAiInput] = useState('');
  const [aiThinking, setAiThinking] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const aiEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatMsgs]);
  useEffect(() => { aiEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [aiMsgs]);

  const now = () => new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const selfMember = members.find(m => m.role === 'owner') || members[0];
  const selfInitials = selfMember?.initials || 'AK';
  const otherActiveMembers = members.filter(m => m.id !== selfMember?.id && m.userId !== selfMember?.userId);
  const onlineCount = isReal
    ? members.filter(m => m.status !== 'offline').length
    : 4;

  // ─── WebRTC Audio Huddle Implementation ──────────────────────────────────────
  const [inHuddle, setInHuddle] = useState(false);
  const [huddleUsers, setHuddleUsers] = useState<string[]>([]);
  const localStreamRef = useRef<MediaStream | null>(null);
  const pcsRef = useRef<Record<string, RTCPeerConnection>>({});

  const [huddleVideo, setHuddleVideo] = useState(false);
  const [huddleMuted, setHuddleMuted] = useState(false);
  const [isSharingScreen, setIsSharingScreen] = useState(false);
  const [huddleState, setHuddleState] = useState<'minimized' | 'normal' | 'fullscreen'>('normal');
  const [screenshareStream, setScreenshareStream] = useState<MediaStream | null>(null);
  const localVideoRef = useRef<HTMLVideoElement | null>(null);
  const screenVideoRef = useRef<HTMLVideoElement | null>(null);
  const fullLocalVideoRef = useRef<HTMLVideoElement | null>(null);
  const fullScreenVideoRef = useRef<HTMLVideoElement | null>(null);
  const [reactions, setReactions] = useState<{ id: number; emoji: string; name: string }[]>([]);
  const [showReactionPicker, setShowReactionPicker] = useState(false);
  const [activeSpeaker, setActiveSpeaker] = useState<string>('You');
  const [huddleTimer, setHuddleTimer] = useState<number>(0);

  const [huddleHasAi, setHuddleHasAi] = useState(false);
  const [transcriptions, setTranscriptions] = useState<Array<{ sender: string; text: string }>>([]);

  const huddleAnalytics = useMemo(() => {
    let sentiment = 75;
    let localCount = 0;
    let aiCount = 0;
    
    transcriptions.forEach(t => {
      const text = t.text.toLowerCase();
      if (text.includes('great') || text.includes('awesome') || text.includes('perfect') || text.includes('resolved') || text.includes('agree') || text.includes('good') || text.includes('nice')) {
        sentiment += 6;
      }
      if (text.includes('error') || text.includes('block') || text.includes('bug') || text.includes('delay') || text.includes('conflict') || text.includes('problem') || text.includes('issue') || text.includes('fail')) {
        sentiment -= 8;
      }
      
      if (t.sender.toLowerCase().includes('you') || t.sender.toLowerCase().includes('me') || t.sender.toLowerCase().includes('aniket')) {
        localCount++;
      } else {
        aiCount++;
      }
    });

    sentiment = Math.max(10, Math.min(100, sentiment));
    const total = localCount + aiCount;
    const localPct = total > 0 ? Math.round((localCount / total) * 100) : 35;
    const aiPct = total > 0 ? Math.round((aiCount / total) * 100) : 65;

    return {
      sentiment,
      localPct,
      aiPct,
      sentimentLabel: sentiment >= 80 ? 'Constructive' : sentiment >= 45 ? 'Calm / Professional' : 'Tense / High Debate'
    };
  }, [transcriptions]);

  useEffect(() => {
    if (!inHuddle) {
      setTranscriptions([]);
      return;
    }
    const mockSnippets = [
      { sender: 'Sneha', text: "Hey team, let's sync on the JWT rotation bug." },
      { sender: 'Arjun', text: "Yeah, the expiry timestamp was offset by 10 minutes in local sessions." },
      { sender: 'Rahul', text: "I'll submit a PR to restrict refreshing of expired tokens." },
      { sender: 'Aisha', text: "Audit suggestion: ensure refresh tokens are encrypted using HMAC-SHA256." }
    ];

    let currentSnippetIdx = 0;
    const interval = setInterval(() => {
      if (currentSnippetIdx < mockSnippets.length) {
        setTranscriptions(prev => [...prev, mockSnippets[currentSnippetIdx]]);
        currentSnippetIdx++;
      }
    }, 6000);

    return () => {
      clearInterval(interval);
    };
  }, [inHuddle]);

  const inviteAiToHuddle = () => {
    if (huddleHasAi) {
      setHuddleHasAi(false);
      setHuddleUsers(prev => prev.filter(u => u !== '🤖'));
      setChatMsgs(prev => [
        ...prev,
        {
          initials: '🤖',
          name: 'Aisha',
          text: 'Aisha has left the huddle session. Talk to you soon!',
          time: now(),
        }
      ]);
      showToast('Teammate Huddle', 'Aisha has left the huddle.', 'info');
    } else {
      setHuddleHasAi(true);
      setHuddleUsers(prev => [...prev, '🤖']);
      setChatMsgs(prev => [
        ...prev,
        {
          initials: '🤖',
          name: 'Aisha',
          text: 'Joined the huddle! "Hello team! I will analyze the active sandbox code edits and whiteboard sketches to help you brainstorm. Try drawing or pasting some code!"',
          time: now(),
        }
      ]);
      showToast('Teammate Huddle', 'Aisha has joined the huddle!', 'success');
    }
  };

  const lastWhiteboardUpdateRef = useRef(0);
  const handleWhiteboardChange = useCallback(() => {
    if (!huddleHasAi) return;
    const nowTime = Date.now();
    if (nowTime - lastWhiteboardUpdateRef.current < 8000) return;
    lastWhiteboardUpdateRef.current = nowTime;

    setTimeout(() => {
      setChatMsgs(prev => [
        ...prev,
        {
          initials: '🤖',
          name: 'Aisha',
          text: `🤖 Whiteboard Analyst: I see you added some strokes. This looks like a server communication architecture. Remember to add client-side circuit breakers and timeout limits if connecting via HTTP!`,
          time: now(),
        }
      ]);
      showToast('AI Whiteboard Review', 'Aisha analyzed the sketch!', 'info');
    }, 3000);
  }, [huddleHasAi]);



  const formatHuddleTime = (sec: number) => {
    const mins = Math.floor(sec / 60);
    const secs = sec % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const triggerReaction = (emoji: string) => {
    const id = Date.now() + Math.random();
    const names = ['You', ...otherActiveMembers.map(m => m.name)];
    const sender = names[Math.floor(Math.random() * names.length)] || 'You';
    setReactions(prev => [...prev, { id, emoji, name: sender }]);
    setTimeout(() => {
      setReactions(prev => prev.filter(r => r.id !== id));
    }, 2500);
    setShowReactionPicker(false);
  };

  useEffect(() => {
    if (localVideoRef.current && localStreamRef.current) {
      localVideoRef.current.srcObject = localStreamRef.current;
    }
  }, [inHuddle, huddleVideo, localStreamRef.current]);

  useEffect(() => {
    if (fullLocalVideoRef.current && localStreamRef.current) {
      fullLocalVideoRef.current.srcObject = localStreamRef.current;
    }
  }, [inHuddle, huddleVideo, huddleState, localStreamRef.current]);

  useEffect(() => {
    if (screenVideoRef.current && screenshareStream) {
      screenVideoRef.current.srcObject = screenshareStream;
    }
  }, [screenshareStream]);

  useEffect(() => {
    if (fullScreenVideoRef.current && screenshareStream) {
      fullScreenVideoRef.current.srcObject = screenshareStream;
    }
  }, [huddleState, screenshareStream]);

  useEffect(() => {
    if (!inHuddle) {
      setHuddleTimer(0);
      return;
    }
    const timer = setInterval(() => {
      setHuddleTimer(t => t + 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [inHuddle]);

  useEffect(() => {
    if (!inHuddle) return;
    const speakers = ['You', ...otherActiveMembers.map(m => m.name)];
    const interval = setInterval(() => {
      const randomSpeaker = speakers[Math.floor(Math.random() * speakers.length)] || 'You';
      setActiveSpeaker(randomSpeaker);
    }, 4500);
    return () => clearInterval(interval);
  }, [inHuddle, members]);

  useEffect(() => {
    return () => {
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(track => track.stop());
      }
      Object.values(pcsRef.current).forEach(pc => pc.close());
    };
  }, []);

  useEffect(() => {
    return () => {
      if (screenshareStream) {
        screenshareStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [screenshareStream]);

  useEffect(() => {
    if (!isReal) return;
    return on('tdash:webrtc:signaled', async (event) => {
      const payload = (event.data || {}) as { slug?: string; senderId?: string; signal?: any };
      if (payload.slug !== teamSlug || !payload.senderId || !payload.signal) return;

      const { senderId, signal } = payload;
      
      if (signal.type === 'join') {
        const pc = createPeerConnection(senderId);
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        sendMessage('tdash:webrtc:signal', { slug: teamSlug, signal: { sdp: offer } });
      } else if (signal.sdp) {
        let pc = pcsRef.current[senderId];
        if (!pc) {
          pc = createPeerConnection(senderId);
        }
        await pc.setRemoteDescription(new RTCSessionDescription(signal.sdp));
        if (signal.sdp.type === 'offer') {
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          sendMessage('tdash:webrtc:signal', { slug: teamSlug, signal: { sdp: answer } });
        }
      } else if (signal.candidate) {
        const pc = pcsRef.current[senderId];
        if (pc) {
          await pc.addIceCandidate(new RTCIceCandidate(signal.candidate));
        }
      } else if (signal.leave) {
        if (pcsRef.current[senderId]) {
          pcsRef.current[senderId].close();
          delete pcsRef.current[senderId];
        }
      }
    });
  }, [on, isReal, teamSlug]);

  const createPeerConnection = (peerId: string) => {
    const pc = new RTCPeerConnection({
      iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
    });

    pcsRef.current[peerId] = pc;

    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => {
        pc.addTrack(track, localStreamRef.current!);
      });
    }

    pc.onicecandidate = (e) => {
      if (e.candidate) {
        sendMessage('tdash:webrtc:signal', { slug: teamSlug, signal: { candidate: e.candidate } });
      }
    };

    pc.ontrack = (e) => {
      const audio = document.createElement('audio');
      audio.srcObject = e.streams[0];
      audio.autoplay = true;
      audio.play().catch(() => {});
    };

    return pc;
  };

  const toggleMute = () => {
    const nextMuted = !huddleMuted;
    if (localStreamRef.current) {
      localStreamRef.current.getAudioTracks().forEach(track => {
        track.enabled = !nextMuted;
      });
    }
    setHuddleMuted(nextMuted);
    showToast('Teammate Huddle', nextMuted ? 'Microphone muted' : 'Microphone unmuted', 'info');
  };

  const toggleCamera = async () => {
    if (huddleVideo) {
      if (localStreamRef.current) {
        localStreamRef.current.getVideoTracks().forEach(track => {
          track.stop();
          localStreamRef.current?.removeTrack(track);
        });
      }
      setHuddleVideo(false);
      showToast('Teammate Huddle', 'Camera turned off', 'info');
    } else {
      try {
        const videoStream = await navigator.mediaDevices.getUserMedia({ video: true });
        const videoTrack = videoStream.getVideoTracks()[0];
        if (localStreamRef.current) {
          localStreamRef.current.addTrack(videoTrack);
          Object.values(pcsRef.current).forEach(pc => {
            const sender = pc.getSenders().find(s => s.track?.kind === 'video');
            if (sender) {
              sender.replaceTrack(videoTrack);
            } else {
              pc.addTrack(videoTrack, localStreamRef.current!);
            }
          });
        }
        setHuddleVideo(true);
        showToast('Teammate Huddle', 'Camera turned on!', 'success');
      } catch {
        showToast('Camera Error', 'Could not access camera.', 'error');
      }
    }
  };

  const toggleScreenshare = async () => {
    if (isSharingScreen) {
      if (screenshareStream) {
        screenshareStream.getTracks().forEach(t => t.stop());
        setScreenshareStream(null);
      }
      setIsSharingScreen(false);
      showToast('Teammate Huddle', 'Screensharing stopped', 'info');
    } else {
      try {
        const stream = await navigator.mediaDevices.getDisplayMedia({ video: true });
        setScreenshareStream(stream);
        setIsSharingScreen(true);
        showToast('Teammate Huddle', 'Screensharing active!', 'success');

        stream.getVideoTracks()[0].onended = () => {
          setScreenshareStream(null);
          setIsSharingScreen(false);
          showToast('Teammate Huddle', 'Screensharing stopped', 'info');
        };
      } catch {
        // Cancelled or denied
      }
    }
  };

  const toggleHuddle = async () => {
    if (inHuddle) {
      setInHuddle(false);
      setHuddleUsers([]);
      setHuddleVideo(false);
      setHuddleMuted(false);
      setHuddleState('normal');
      setReactions([]);
      setShowReactionPicker(false);
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(track => track.stop());
        localStreamRef.current = null;
      }
      if (screenshareStream) {
        screenshareStream.getTracks().forEach(track => track.stop());
        setScreenshareStream(null);
      }
      setIsSharingScreen(false);
      Object.keys(pcsRef.current).forEach(peerId => {
        if (pcsRef.current[peerId]) {
          pcsRef.current[peerId].close();
          delete pcsRef.current[peerId];
        }
      });
      if (isReal) {
        sendMessage('tdash:webrtc:signal', { slug: teamSlug, signal: { leave: true } });
      }
      showToast('Teammate Huddle', 'Left huddle discussion channel', 'info');
    } else {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        localStreamRef.current = stream;
        setInHuddle(true);
        if (!isReal) {
          const otherInitials = otherActiveMembers.map(m => m.initials || m.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase());
          setHuddleUsers([selfInitials, ...otherInitials]);
        } else {
          setHuddleUsers([selfInitials]);
          sendMessage('tdash:webrtc:signal', { slug: teamSlug, signal: { type: 'join' } });
        }
        showToast('Teammate Huddle', 'Connected to audio discussion channel!', 'success');

        try {
          const videoStream = await navigator.mediaDevices.getUserMedia({ video: true });
          const videoTrack = videoStream.getVideoTracks()[0];
          stream.addTrack(videoTrack);
          setHuddleVideo(true);
        } catch {
          // Camera not found or denied, proceed with audio only
        }
      } catch {
        showToast('Huddle Connection Error', 'Microphone access is required for Huddle.', 'error');
      }
    }
  };

  // ─── Collaborative Code Sandbox Implementation ─────────────────────────────
  const [activeFile, setActiveFile] = useState('index.html');
  const [sandboxFiles, setSandboxFiles] = useState<Record<string, string>>({
    'index.html': '<!-- Collaborative Sandbox -->\n<div class="card">\n  <h1>Hello World</h1>\n</div>',
    'style.css': '/* Custom Styles */\n.card {\n  padding: 20px;\n  background: #f9f9f9;\n}',
    'app.js': '// Logic\nconsole.log("Ready!");'
  });

  const handleSandboxChange = (newVal: string) => {
    setSandboxFiles(prev => {
      const next = { ...prev, [activeFile]: newVal };
      if (isReal) {
        sendMessage('tdash:sandbox:update', { slug: teamSlug, filename: activeFile, code: newVal });
      }
      return next;
    });
  };

  useEffect(() => {
    if (!isReal) return;
    return on('tdash:sandbox:updated', (event) => {
      const payload = (event.data || {}) as { slug?: string; filename?: string; code?: string };
      if (payload.slug !== teamSlug || !payload.filename || payload.code === undefined) return;
      setSandboxFiles(prev => ({
        ...prev,
        [payload.filename!]: payload.code!
      }));
    });
  }, [on, isReal, teamSlug]);

  const lastCodeRef = useRef<Record<string, string>>({});
  useEffect(() => {
    const currentCode = sandboxFiles[activeFile] || '';
    const previousCode = lastCodeRef.current[activeFile] || '';
    if (!huddleHasAi || !currentCode || currentCode === previousCode) {
      lastCodeRef.current[activeFile] = currentCode;
      return;
    }
    lastCodeRef.current[activeFile] = currentCode;

    const t = setTimeout(async () => {
      showToast('AI Code Review', 'Aisha is reviewing your sandbox edits...', 'info');
      try {
        const res = await apiClient.explainCode(currentCode, 'javascript');
        if (res.success && res.data?.explanation) {
          setChatMsgs(prev => [
            ...prev,
            {
              initials: '🤖',
              name: 'Aisha',
              text: `🤖 Sandbox Code Review (${activeFile}):\n${res.data.explanation.slice(0, 350)}...`,
              time: now(),
            }
          ]);
        }
      } catch {
        setChatMsgs(prev => [
          ...prev,
          {
            initials: '🤖',
            name: 'Aisha',
            text: `🤖 Sandbox Code Review: I analyzed your changes to ${activeFile}. It looks clean and compiles successfully! Good job.`,
            time: now(),
          }
        ]);
      }
    }, 6000);

    return () => clearTimeout(t);
  }, [sandboxFiles[activeFile], activeFile, huddleHasAi]);

  const [aiSandboxLoading, setAiSandboxLoading] = useState(false);
  const triggerSandboxAI = async () => {
    const code = sandboxFiles[activeFile];
    if (!code.trim() || aiSandboxLoading) return;
    setAiSandboxLoading(true);
    showToast('Aisha', 'Reviewing code sandbox...', 'info');
    try {
      const configuredBase = import.meta.env.VITE_API_URL ? import.meta.env.VITE_API_URL.replace(/\/$/, '') : '';
      const base = configuredBase || (typeof window !== 'undefined' && window.location.hostname === 'localhost' ? 'http://localhost:3001/api' : '/api');
      const aiChatUrl = `${base}/ai/chat`;

      const response = await fetch(aiChatUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('scrudix_token') ? { 'Authorization': `Bearer ${localStorage.getItem('scrudix_token')}` } : {})
        },
        body: JSON.stringify({
          messages: [{ role: 'user', content: `Please review and improve this code snippet:\n\`\`\`\n${code}\n\`\`\`\nProvide only the corrected and optimized code block without comments or markdown formatting, so it can be pasted directly.` }],
          systemPrompt: "You are an automated code assistant. Return ONLY the code inside the code snippet. Do not include markdown code block syntax (like ```javascript) or wrapping text."
        }),
      });

      if (!response.ok) throw new Error();
      const json = await response.json();
      const corrected = json?.data?.response || json?.response;
      if (corrected) {
        const cleaned = corrected.replace(/^```[a-zA-Z]*\n/, '').replace(/\n```$/, '');
        setSandboxFiles(prev => {
          const next = { ...prev, [activeFile]: cleaned };
          if (isReal) {
            sendMessage('tdash:sandbox:update', { slug: teamSlug, filename: activeFile, code: cleaned });
          }
          return next;
        });
        showToast('Aisha', 'Code optimized successfully!', 'success');
      }
    } catch {
      showToast('Aisha', 'Failed to connect to correction service.', 'error');
    } finally {
      setAiSandboxLoading(false);
    }
  };

  const saveRoadmap = useCallback(async (cols: RoadmapCol[]) => {
    if (!isReal) return;
    try {
      await apiPut(`/${teamSlug}/roadmap`, { roadmap: cols });
    } catch {
      showToast('Roadmap', 'Could not save board changes', 'error');
    }
  }, [isReal, teamSlug, showToast]);

  // Listen for real-time team chat messages from other members.
  useEffect(() => {
    if (!isReal) return;
    return on('tdash:chat:message', (event) => {
      const data = (event.data || {}) as { slug?: string; message?: ActivityItem };
      if (data.slug !== teamSlug) return;
      const msg = data.message;
      if (!msg || msg.action !== 'chat-message') return;
      const stamp = msg.timestamp ? new Date(msg.timestamp) : new Date();
      const initials = msg.memberInitials || '??';
      // Our own messages are already optimistically added locally.
      if (initials === selfInitials) return;
      setChatMsgs(prev => {
        const rawName = msg.memberName || initials;
        const cleanedName = cleanChatName(rawName);
        const cleanedInitials = cleanChatInitials(initials, rawName);
        if (prev.some(m => m.text === (msg.detail || '') && m.name === cleanedName && m.time === stamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }))) {
          return prev;
        }
        return [...prev, {
          initials: cleanedInitials,
          name: cleanedName,
          text: msg.detail || '',
          time: stamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          self: initials === selfInitials,
        }];
      });
    });
  }, [on, isReal, teamSlug, selfInitials]);

  // Listen for real-time roadmap updates from other members.
  useEffect(() => {
    if (!isReal) return;
    return on('tdash:roadmap:updated', (event) => {
      const data = (event.data || {}) as { slug?: string; roadmap?: RoadmapCol[] };
      if (data.slug !== teamSlug || !Array.isArray(data.roadmap)) return;
      // Avoid overwriting while the user is actively dragging.
      if (draggingCardId) return;
      setRoadmapCols(data.roadmap);
    });
  }, [on, isReal, teamSlug, draggingCardId]);

  useEffect(() => {
    if (isReal) {
      setChatMsgs([]);
      setChatImages([]);
      // Load persisted roadmap; if empty, seed the default columns.
      void (async () => {
        try {
          const json = await apiGet<unknown>(`/${teamSlug}/roadmap`);
          const cols: RoadmapCol[] =
            json && typeof json === 'object' && 'data' in (json as Record<string, unknown>)
              ? ((json as { data?: RoadmapCol[] }).data || [])
              : [];
          if (cols.length > 0) {
            setRoadmapCols(cols);
          } else {
            const seeded = cloneRoadmapCols(ROADMAP_COLS);
            setRoadmapCols(seeded);
            await apiPut(`/${teamSlug}/roadmap`, { roadmap: seeded });
          }
        } catch {
          setRoadmapCols(cloneRoadmapCols(ROADMAP_COLS));
        }
      })();
      return;
    }
    setChatMsgs(INIT_CHAT);
    setRoadmapCols(cloneRoadmapCols(ROADMAP_COLS));
  }, [isReal, teamSlug]);

  useEffect(() => {
    if (!isReal) return;
    let stopped = false;

    const loadChat = async () => {
      try {
        const json = await apiGet<unknown>(`/${teamSlug}/activity?limit=120`);
        const rows: ActivityItem[] =
          json && typeof json === 'object' && 'data' in (json as Record<string, unknown>)
            ? ((json as { data?: ActivityItem[] }).data || [])
            : (Array.isArray(json) ? json : []);
        const msgs = rows
          .filter((row) => row.type === 'meeting' && row.action === 'chat-message' && (row.detail || '').trim().length > 0)
          .slice()
          .reverse()
          .map((row) => {
            const stamp = row.timestamp ? new Date(row.timestamp) : new Date();
            const initials = row.memberInitials || '??';
            const rawName = row.memberName || initials;
            const cleanedName = cleanChatName(rawName);
            const cleanedInitials = cleanChatInitials(initials, rawName);
            return {
              initials: cleanedInitials,
              name: cleanedName,
              text: row.detail || '',
              time: stamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
              self: initials === selfInitials,
            } as ChatMsg;
          });
        if (!stopped) setChatMsgs(msgs);
      } catch {
        // Keep chat usable even if polling fails temporarily.
      }
    };

    loadChat();
    const interval = setInterval(loadChat, 4000);
    return () => {
      stopped = true;
      clearInterval(interval);
    };
  }, [isReal, teamSlug, selfInitials]);

  function readFileAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(new Error('Failed to read image'));
      reader.readAsDataURL(file);
    });
  }

  async function handleChatPaste(e: React.ClipboardEvent<HTMLTextAreaElement>) {
    const items = Array.from(e.clipboardData?.items || []);
    const imageItems = items.filter(item => item.type.startsWith('image/'));
    if (!imageItems.length) return;
    e.preventDefault();
    try {
      const urls = await Promise.all(
        imageItems
          .map(item => item.getAsFile())
          .filter((f): f is File => !!f)
          .map(readFileAsDataUrl),
      );
      if (!urls.length) return;
      setChatImages(prev => [...prev, ...urls].slice(0, 4));
      showToast('Image pasted', `${urls.length} image${urls.length > 1 ? 's' : ''} added`, 'success');
    } catch {
      showToast('Paste failed', 'Could not read pasted image', 'error');
    }
  }

  const AI_ANSWERS: Record<string, string> = {
    merge: "## Merge Readiness\n\nPR #13 by **Rahul** has been through **3 review cycles**.\n\n- ✅ Tests passing\n- ✅ Code review approved\n- ⚠️ Recommend one more pass on **lines 45–67** for edge-case handling\n\n**Verdict:** Safe to merge after the final edge-case check.",
    auth: "## Auth Update\n\nAuth service activity is down to **8 commits this week** (from 22 last week).\n\n- **JWT refresh flow** is the current focus\n- **Rahul** is handling edge-case cleanup\n- No blockers flagged for staging deployment\n\n**Next step:** Review Rahul's refresh-token PR once it's open.",
    risk: "## Sprint Risks\n\n- 🔴 **Auth Service v2** is at **88% budget** with an **Apr 30** deadline\n- 🟡 PR #18 by Rahul has been open for **4 days**\n- 🟢 No other critical blockers detected\n\n**Recommendation:** Daily check-ins with Rahul until PR #18 lands.",
    quality: "## Quality Snapshot\n\n| Member | Score | Status |\n|--------|------:|--------|\n| Priya  | 94    | ✅     |\n| Arjun  | 87    | ✅     |\n| Sneha  | 74    | ⚠️     |\n| Rahul  | 38    | 🔴     |\n\n**Sprint average:** 76/100\n**Velocity:** ↑18% vs last sprint\n\n**Action:** Pair Rahul with Priya for code-quality mentorship.",
    default: "## Team Status\n\n- **14 tasks done**, **5 in progress**\n- Budget burn is healthy at **₹570/hr avg**\n- No critical blockers detected\n\nThe team is on track for the current sprint.",
  };

  const sendChat = () => {
    const txt = chatInput.trim();
    if (!txt && !chatImages.length) return;
    const rawName = selfMember?.name || 'You';
    const cleanedName = cleanChatName(rawName);
    const cleanedInitials = cleanChatInitials(selfInitials, rawName);
    setChatMsgs(prev => [...prev, { initials: cleanedInitials, name: cleanedName, text: txt, images: chatImages.length ? [...chatImages] : undefined, time: now(), self: true }]);
    setChatInput('');
    setChatImages([]);
    
    if (txt.toLowerCase().includes('@scrudix')) {
      const queryLower = txt.toLowerCase();
      let responseText = "Hello! I am analyzing the repository context to assist you. Ask me about quality, merge status, or sprint risk checks!";
      
      if (queryLower.includes('quality') || queryLower.includes('score')) {
        responseText = "📊 **Scrudix Quality Analyst:** Average sprint quality is **76/100**. Priya leads with **94**, while Rahul is currently at **38**. Recommend pairing Rahul with Priya for mentorship.";
      } else if (queryLower.includes('merge') || queryLower.includes('pr')) {
        responseText = "🔀 **Scrudix Merge Auditor:** PR #13 has passed all 3 review stages. Minor lock warning found on lines 45-67. Safe to merge after double checking JWT persistence refresh callbacks.";
      } else if (queryLower.includes('risk') || queryLower.includes('budget')) {
        responseText = "⚠️ **Scrudix Risk Manager:** Auth service v2 budget burn rate is steep (at **88%** limits). Milestone deadline is **Apr 30**. High risk warning flagged for PR #18.";
      }

      setTimeout(() => {
        setChatMsgs(prev => [
          ...prev,
          {
            initials: '🤖',
            name: 'Aisha',
            text: responseText,
            time: now()
          }
        ]);
        showToast('AI Worker Chat Response', 'Aisha answered in channel!', 'info');
      }, 1000);
    }

    if (isReal) {
      void apiPost(`/${teamSlug}/activity`, {
        memberId: selfMember?.id,
        action: 'chat-message',
        detail: txt,
        type: 'meeting',
        cost: 0,
      });
    }
  };
  const sendThreadReply = () => {
    if (!threadInput.trim() || !activeThreadMsg) return;
    const rawName = selfMember?.name || 'You';
    const cleanedName = cleanChatName(rawName);
    const cleanedInitials = cleanChatInitials(selfInitials, rawName);
    const newReply = {
      initials: cleanedInitials,
      name: cleanedName,
      text: threadInput.trim(),
      time: now(),
      self: true
    };
    setThreadReplies(prev => ({
      ...prev,
      [activeThreadMsg.text]: [...(prev[activeThreadMsg.text] || []), newReply]
    }));
    setThreadInput('');
    showToast('Thread reply', 'Reply sent to message thread', 'success');
  };

  const sendAI = async (q?: string) => {
    const txt = (q || aiInput).trim(); if (!txt || aiThinking) return;
    setAiMsgs(prev => [...prev, { role: 'user', text: txt }]);
    setAiInput(''); setAiThinking(true);
    // Broadcast Aisha presence to the team dashboard
    window.dispatchEvent(new CustomEvent('aisha:activity', { detail: { active: true, activity: 'analyzing team data' } }));

    // Instant structured answers for known quick prompts
    if (q && AI_ANSWERS[q]) {
      await new Promise(r => setTimeout(r, 400));
      setAiMsgs(prev => [...prev, { role: 'bot', text: AI_ANSWERS[q] }]);
      setAiThinking(false);
    window.dispatchEvent(new CustomEvent('aisha:activity', { detail: { active: false } }));
      return;
    }

    try {
      const configuredBase = import.meta.env.VITE_API_URL ? import.meta.env.VITE_API_URL.replace(/\/$/, '') : '';
      const base = configuredBase || (typeof window !== 'undefined' && window.location.hostname === 'localhost' ? 'http://localhost:3001/api' : '/api');
      const aiChatUrl = `${base}/ai/chat`;

      const token = typeof window !== 'undefined' ? localStorage.getItem('scrudix_token') : null;
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const systemPrompt = `You are Aisha, the team intelligence assistant for the Scrudix Team Dashboard.
You have access to the current team state:
- Team Slug: ${teamSlug}
- Members: ${JSON.stringify(members.map(m => ({ name: m.name, role: m.role, status: m.status, commits: m.commits })))}
- Kanban Sprint Roadmap: ${JSON.stringify(roadmapCols.map(c => ({ column: c.title, tasks: c.items.map(i => ({ title: i.title, desc: i.desc, priority: i.priority, assignee: i.assignee })) })))}

Provide helpful, context-aware, professional, and actionable insights about blockers, task assignments, budget burn, or velocity.

Format your response using Markdown: use **bold** for emphasis, bullet points for lists, and short headings (##) for sections. Keep responses concise (2-4 bullet points or 1-3 sentences).`;

      const messagesPayload = aiMsgs.map(m => ({
        role: m.role === 'user' ? 'user' : 'model',
        content: typeof m.text === 'string' ? m.text : (m.text as any)?.props?.children || ''
      }));
      messagesPayload.push({ role: 'user', content: txt });

      const response = await fetch(aiChatUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          messages: messagesPayload,
          systemPrompt,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to query AI: ${response.status}`);
      }

      const json = await response.json();
      const botText = json?.data?.response || json?.response || "I processed your request but didn't receive a response.";

      setAiMsgs(prev => [...prev, { role: 'bot', text: botText }]);
    } catch (err) {
      console.error('[AI] Failed to fetch LLM response:', err);
      setAiMsgs(prev => [...prev, { role: 'bot', text: "Error connecting to AI intelligence services. Please try again." }]);
    } finally {
      setAiThinking(false);
    window.dispatchEvent(new CustomEvent('aisha:activity', { detail: { active: false } }));
    }
  };

  const TABS: { id: DiscTab; label: string; icon: React.ReactNode }[] = [
    { id: 'chat', label: 'Team Chat', icon: <svg width="11" height="11" viewBox="0 0 16 16" fill="none"><path d="M3 3h10v7H6l-3 3V3z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/></svg> },
    { id: 'roadmap', label: 'Roadmap', icon: <svg width="11" height="11" viewBox="0 0 16 16" fill="none"><path d="M2.5 3.5h11M2.5 8h8M2.5 12.5h6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg> },
    { id: 'ai', label: 'Aisha', icon: <svg width="11" height="11" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="5.5" stroke="currentColor" strokeWidth="1.3"/><circle cx="8" cy="8" r="1.7" stroke="currentColor" strokeWidth="1.3"/></svg> },
    { id: 'whiteboard', label: 'Whiteboard', icon: <svg width="11" height="11" viewBox="0 0 16 16" fill="none"><rect x="2" y="3" width="12" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.3"/><path d="M5 13h6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg> },
    { id: 'sandbox', label: 'Code Sandbox', icon: <svg width="11" height="11" viewBox="0 0 16 16" fill="none"><path d="M4 2.5L1.5 5 4 7.5M12 2.5l2.5 2.5-2.5 2.5M9.5 1.5l-3 7" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg> },
  ];

  const QUICK = [
    { label: 'Merge PR?', q: 'merge' },
    { label: 'Auth this week', q: 'auth' },
    { label: 'Sprint risks', q: 'risk' },
    { label: 'Team quality', q: 'quality' },
  ];

  function addRoadmapItem() {
    const title = newItem.title.trim();
    if (!title) {
      showToast('Roadmap', 'Item title is required', 'error');
      return;
    }
    const item: RoadmapItem = {
      id: crypto.randomUUID(),
      title,
      desc: 'new roadmap task',
      tag: newItem.priority === 'high' ? 'critical' : newItem.priority === 'low' ? 'minor' : 'task',
      tagC: newItem.priority === 'high' ? 'r' : newItem.priority === 'low' ? 'n' : 'a',
      assignee: selfInitials,
      priority: newItem.priority,
      time: 'now',
    };
    setRoadmapCols(prev => {
      const next = prev.map(col => col.id === newItem.column ? { ...col, items: [item, ...col.items] } : col);
      void saveRoadmap(next);
      return next;
    });
    setShowAddItem(false);
    setNewItem({ title: '', column: 'backlog', priority: 'medium' });
    showToast('Roadmap', 'Item added to board', 'success');
  }

  function onDragStart(fromCol: RoadmapColId, item: RoadmapItem) {
    dragItem.current = { fromCol, item };
    setDraggingCardId(`${fromCol}-${item.title}`);
  }

  function onDrop(toCol: RoadmapColId) {
    const payload = dragItem.current;
    setDragOverCol(null);
    if (!payload) return;
    if (payload.fromCol === toCol) return;
    const movedId = `${payload.fromCol}-${payload.item.title}`;
    setRoadmapCols(prev => {
      const next = prev.map(col => {
        if (col.id === payload.fromCol) return { ...col, items: col.items.filter(i => i !== payload.item) };
        if (col.id === toCol) return { ...col, items: [payload.item, ...col.items] };
        return col;
      });
      void saveRoadmap(next);
      return next;
    });
    dragItem.current = null;
    setDropFlashCol(toCol);
    setLastDroppedCardId(`${toCol}-${payload.item.title}`);
    setDraggingCardId(null);
    setTimeout(() => setDropFlashCol(null), 380);
    setTimeout(() => setLastDroppedCardId(null), 520);
    showToast('Roadmap', `Card moved to ${roadmapCols.find(c => c.id === toCol)?.title ?? toCol}`, 'success');
  }

  return (
    <div className="td-discussion disc-sheet" data-theme={theme} style={{ display: 'flex', flexDirection: 'column', height: '100%', flex: 1, overflow: 'hidden' }}>
      <div style={{ padding: '10px 16px 8px', borderBottom: '1px solid var(--border)', background: 'var(--bg1)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontSize: 10, fontFamily: 'var(--mono)', color: 'var(--text3)', letterSpacing: '.3px' }}>
          scrudix / team / discussion room
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--green)', boxShadow: '0 0 6px var(--green)' }} />
          <span style={{ fontSize: 10, fontFamily: 'var(--mono)', color: 'var(--green)' }}>LIVE</span>
        </div>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid var(--border)', background: 'var(--bg1)', flexShrink: 0, height: 44, paddingLeft: 4 }}>
        {TABS.map(t => (
          <button key={t.id} className={`disc-tab${tab === t.id ? ' active' : ''}`} onClick={() => setTab(t.id)}><span style={{ display: 'inline-flex' }}>{t.icon}</span>{t.label}</button>
        ))}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12, paddingRight: 16 }}>
          {/* Voice Huddle Button */}
          <button
            onClick={toggleHuddle}
            className={inHuddle ? "btn-p" : "btn-s"}
            style={{
              fontSize: 10,
              padding: '4px 10px',
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              borderRadius: 20,
              background: inHuddle ? '#ef4444' : undefined,
              borderColor: inHuddle ? '#ef4444' : undefined,
              color: inHuddle ? '#fff' : undefined,
            }}
          >
            <svg width="10" height="10" viewBox="0 0 16 16" fill="currentColor">
              {inHuddle ? (
                <path d="M8 12c2.2 0 4-1.8 4-4H4c0 2.2 1.8 4 4 4zm0-10C5.8 2 4 3.8 4 6v2c0 2.2 1.8 4 4 4s4-1.8 4-4V6c0-2.2-1.8-4-4-4z" />
              ) : (
                <path d="M8 11c1.66 0 3-1.34 3-3V3c0-1.66-1.34-3-3-3S5 1.34 5 3v5c0 1.66 1.34 3 3 3zm5-3c0 2.76-2.24 5-5 5s-5-2.24-5-5H2c0 3.04 2.19 5.57 5 5.93V15h2v-2.07c2.81-.36 5-2.89 5-5.93h-1z" />
              )}
            </svg>
            {inHuddle ? "Live Huddle" : "Join Huddle"}
          </button>

          {inHuddle && (
            <button
              onClick={inviteAiToHuddle}
              className="btn-p"
              style={{
                fontSize: 10,
                padding: '4px 10px',
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                borderRadius: 20,
                background: huddleHasAi ? '#3ecf8e' : undefined,
                borderColor: huddleHasAi ? '#3ecf8e' : undefined,
                color: huddleHasAi ? '#fff' : undefined,
              }}
            >
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2v2M5 5l1.5 1.5M19 5l-1.5 1.5" />
                <rect x="3" y="11" width="18" height="10" rx="3" />
                <path d="M7 15h10M9 11v3M15 11v3" />
              </svg>
              <span>{huddleHasAi ? "Aisha Active" : "Invite Aisha"}</span>
            </button>
          )}

          {inHuddle && huddleUsers.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {huddleUsers.map((u, i) => (
                <div key={i} className="avr avsm" style={{ width: 18, height: 18, fontSize: 8, background: 'var(--bg3)', border: '1px solid var(--border)', marginLeft: i > 0 ? -6 : 0 }} title={`${u} in huddle`}>
                  {u}
                </div>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--green)', boxShadow: '0 0 5px var(--green)' }} />
            <span style={{ fontSize: 10, fontFamily: 'var(--mono)', color: 'var(--green)' }}>{onlineCount} online</span>
          </div>
        </div>
      </div>

      {/* ── CHAT ── */}
      {tab === 'chat' && (
        <div style={{ flex: 1, display: 'flex', overflow: 'hidden', position: 'relative' }}>
          
          {/* Main Chat Area */}
          <div style={{ flex: activeThreadMsg ? '0 0 65%' : '1 1 100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRight: activeThreadMsg ? '1px solid var(--border)' : 'none' }}>
            <div style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
              {chatMsgs.map((msg, i) => {
                const replies = threadReplies[msg.text] || [];
                return (
                  <div 
                    key={i} 
                    className="group"
                    style={{ 
                      display: 'flex', 
                      alignItems: 'flex-start', 
                      gap: 10, 
                      flexDirection: (msg.self || msg.initials === selfInitials) ? 'row-reverse' : 'row',
                      position: 'relative'
                    }}
                  >
                    <div className="avr avsm" style={{ background: MBG[msg.initials] || 'var(--bg3)', color: MFG[msg.initials] || 'var(--text)', flexShrink: 0 }}>{msg.initials}</div>
                    
                    <div style={{ maxWidth: '70%', position: 'relative' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3, flexDirection: (msg.self || msg.initials === selfInitials) ? 'row-reverse' : 'row' }}>
                        <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--text2)' }}>{msg.name}</span>
                        <span style={{ fontSize: 9, fontFamily: 'var(--mono)', color: 'var(--text4)' }}>{msg.time}</span>
                      </div>
                      
                      <div style={{ position: 'relative' }}>
                        <div style={{ padding: '8px 12px', background: (msg.self || msg.initials === selfInitials) ? 'rgba(79, 57, 227, 0.08)' : 'var(--bg2)', border: `1px solid ${(msg.self || msg.initials === selfInitials) ? 'rgba(79, 57, 227, 0.2)' : 'var(--border)'}`, borderRadius: (msg.self || msg.initials === selfInitials) ? '12px 3px 12px 12px' : '3px 12px 12px 12px', fontSize: 12.5, color: 'var(--text)', lineHeight: 1.55 }}>
                          {msg.images && msg.images.length > 0 && (
                            <div className="chat-img-grid" style={{ marginBottom: msg.text ? 8 : 0 }}>
                              {msg.images.map((img, imgIdx) => (
                                <img key={`${i}-${imgIdx}`} src={img} alt="attachment" className="chat-img-item" />
                              ))}
                            </div>
                          )}
                          {msg.text}
                        </div>

                        {/* Hover Quick Action Panel (Reactions + Reply) */}
                        <div className="opacity-0 group-hover:opacity-100 absolute -top-8 right-0 bg-white dark:bg-zinc-900 border border-slate-100 dark:border-zinc-800 rounded-lg shadow-sm px-2 py-1 flex items-center gap-1.5 z-10 transition-opacity duration-200">
                          {['👍', '❤️', '😂', '🎉'].map(emoji => (
                            <button
                              key={emoji}
                              onClick={() => {
                                setChatReactions(prev => {
                                  const forMsg = prev[msg.text] || {};
                                  return { ...prev, [msg.text]: { ...forMsg, [emoji]: (forMsg[emoji] || 0) + 1 } };
                                });
                                triggerReaction(emoji);
                              }}
                              style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                              className="hover:scale-125 transition-transform duration-150 text-[11px] p-0.5"
                            >
                              {emoji}
                            </button>
                          ))}
                          <button
                            onClick={() => setActiveThreadMsg(msg)}
                            className="text-[10px] text-zinc-500 hover:text-[#4f39e3] hover:font-bold border-l border-slate-150 dark:border-zinc-800 pl-1.5 ml-1 flex items-center gap-1 font-semibold bg-transparent"
                          >
                            💬 Reply
                          </button>
                        </div>
                      </div>

                      {/* Displayed Emoji Reactions Pills */}
                      {chatReactions[msg.text] && (
                        <div className="flex gap-1 mt-1.5 flex-wrap" style={{ justifyContent: (msg.self || msg.initials === selfInitials) ? 'flex-end' : 'flex-start' }}>
                          {Object.entries(chatReactions[msg.text]).map(([emoji, count]) => (
                            <span 
                              key={emoji} 
                              onClick={() => {
                                setChatReactions(prev => {
                                  const forMsg = prev[msg.text] || {};
                                  return { ...prev, [msg.text]: { ...forMsg, [emoji]: forMsg[emoji] + 1 } };
                                });
                                triggerReaction(emoji);
                              }}
                              className="cursor-pointer inline-flex items-center gap-1 text-[9px] bg-slate-50 dark:bg-zinc-800 border border-slate-100 dark:border-zinc-850 px-1.5 py-0.5 rounded-full font-bold hover:bg-[#4f39e3]/5 hover:border-[#4f39e3]/30 text-zinc-650 dark:text-zinc-450"
                            >
                              {emoji} <span className="text-[8px] opacity-75">{count}</span>
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Thread Reply count link */}
                      {replies.length > 0 && (
                        <button 
                          onClick={() => setActiveThreadMsg(msg)} 
                          className="text-[10px] text-[#4f39e3] dark:text-[#818cf8] hover:underline font-bold mt-1.5 flex items-center gap-1 bg-transparent"
                        >
                          💬 {replies.length} {replies.length === 1 ? 'reply' : 'replies'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
              <div ref={chatEndRef} />
            </div>

            {/* Compose footer */}
            <div style={{ padding: '10px 14px', borderTop: '1px solid var(--border)', background: 'var(--bg1)', flexShrink: 0 }}>
              {chatImages.length > 0 && (
                <div className="chat-compose-previews">
                  {chatImages.map((img, idx) => (
                    <div key={idx} className="chat-compose-thumb-wrap">
                      <img src={img} alt={`pasted-${idx}`} className="chat-compose-thumb" />
                      <button className="chat-compose-thumb-remove" onClick={() => setChatImages(prev => prev.filter((_, i) => i !== idx))}>×</button>
                    </div>
                  ))}
                </div>
              )}
              <div className="chat-compose-row">
                <textarea
                  className="inp"
                  value={chatInput}
                  onChange={e => setChatInput(e.target.value)}
                  onPaste={handleChatPaste}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendChat();
                    }
                  }}
                  placeholder="Type a message... Ctrl+V to paste image, Shift+Enter for newline"
                  rows={1}
                  style={{ flex: 1, resize: 'none', minHeight: 38, maxHeight: 110, paddingTop: 10, paddingBottom: 10 }}
                />
                <button className="btn-p" style={{ padding: '8px 14px', fontSize: 12, alignSelf: 'flex-end' }} onClick={sendChat}>Send</button>
              </div>
            </div>
          </div>

          {/* Right Thread Sidebar Pane */}
          {activeThreadMsg && (
            <div style={{ flex: '0 0 35%', display: 'flex', flexDirection: 'column', overflow: 'hidden', background: theme === 'light' ? '#fbfbfe' : '#0b0914', borderLeft: '1px solid var(--border)' }}>
              {/* Header */}
              <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--bg1)' }}>
                <div>
                  <span className="text-xs font-bold text-zinc-800 dark:text-white uppercase tracking-wider font-mono">Thread replies</span>
                </div>
                <button 
                  onClick={() => setActiveThreadMsg(null)}
                  style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 16, fontWeight: 700 }}
                >
                  ×
                </button>
              </div>

              {/* Thread Content */}
              <div style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
                {/* Parent Message Header */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, paddingBottom: 16, borderBottom: '1px solid var(--border)' }}>
                  <div className="avr avsm" style={{ background: MBG[activeThreadMsg.initials] || 'var(--bg3)', color: MFG[activeThreadMsg.initials] || 'var(--text)', flexShrink: 0 }}>{activeThreadMsg.initials}</div>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text2)' }}>{activeThreadMsg.name}</span>
                      <span style={{ fontSize: 9, fontFamily: 'var(--mono)', color: 'var(--text4)' }}>{activeThreadMsg.time}</span>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text)', lineHeight: 1.5 }}>{activeThreadMsg.text}</div>
                  </div>
                </div>

                {/* Replies List */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {(threadReplies[activeThreadMsg.text] || []).map((reply, replyIdx) => (
                    <div key={replyIdx} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                      <div className="avr avsm" style={{ width: 24, height: 24, fontSize: 9, background: MBG[reply.initials] || 'var(--bg3)', color: MFG[reply.initials] || 'var(--text)', flexShrink: 0 }}>{reply.initials}</div>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 1 }}>
                          <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text2)' }}>{reply.name}</span>
                          <span style={{ fontSize: 8, fontFamily: 'var(--mono)', color: 'var(--text4)' }}>{reply.time}</span>
                        </div>
                        <div style={{ fontSize: 11.5, color: 'var(--text)', lineHeight: 1.45 }}>{reply.text}</div>
                      </div>
                    </div>
                  ))}
                  {(threadReplies[activeThreadMsg.text] || []).length === 0 && (
                    <div style={{ padding: '24px 8px', textAlign: 'center', fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>
                      No replies yet. Be the first to start the thread conversation!
                    </div>
                  )}
                </div>
              </div>

              {/* Thread Input box */}
              <div style={{ padding: '10px 14px', borderTop: '1px solid var(--border)', background: 'var(--bg1)', flexShrink: 0 }}>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input
                    type="text"
                    className="inp"
                    value={threadInput}
                    onChange={e => setThreadInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        sendThreadReply();
                      }
                    }}
                    placeholder="Reply to thread..."
                    style={{ flex: 1, fontSize: 11.5 }}
                  />
                  <button 
                    onClick={sendThreadReply}
                    className="btn-p" 
                    style={{ padding: '6px 12px', fontSize: 11 }}
                  >
                    Reply
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── ROADMAP ── */}
      {tab === 'roadmap' && (
        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 18, fontWeight: 600, letterSpacing: -.5, color: 'var(--text)' }}>Sprint Roadmap</div>
              <div style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--text3)', marginTop: 2 }}>Sprint 4 · Apr 22 – May 5</div>
            </div>
            <button className="btn-p" style={{ fontSize: 11, padding: '6px 14px' }} onClick={() => setShowAddItem(true)}>+ Add item</button>
          </div>
          {/* Sprint velocity bar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14, padding: '10px 14px', borderRadius: 10, background: 'var(--bg2)', border: '1px solid var(--border)' }}>
            {(() => {
              const total = roadmapCols.reduce((acc, c) => acc + c.items.length, 0);
              const done = roadmapCols.find(c => c.id === 'done')?.items.length ?? 0;
              const progress = roadmapCols.find(c => c.id === 'progress')?.items.length ?? 0;
              const review = roadmapCols.find(c => c.id === 'review')?.items.length ?? 0;
              const pct = total > 0 ? Math.round((done / total) * 100) : 0;
              return (
                <>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: 10, fontFamily: 'var(--mono)', color: 'var(--text3)' }}>
                        Sprint velocity — {done}/{total} done · {progress} in progress · {review} in review
                      </span>
                      <span style={{ fontSize: 11, fontWeight: 700, color: pct >= 70 ? 'var(--green)' : pct >= 40 ? 'var(--amber)' : 'var(--text)' }}>{pct}%</span>
                    </div>
                    <div style={{ height: 6, borderRadius: 3, background: 'var(--border)', overflow: 'hidden' }}>
                      <div style={{ height: '100%', borderRadius: 3, width: `${pct}%`, background: pct >= 70 ? 'var(--green)' : 'linear-gradient(90deg, var(--accent), var(--accent2))', transition: 'width .5s ease' }} />
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, alignItems: 'start' }}>
            {isReal && roadmapCols.length === 0 && (
              <div className="card" style={{ gridColumn: '1 / -1', padding: 14 }}>
                <div style={{ fontSize: 12, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>
                  Real mode active: no demo roadmap loaded yet.
                </div>
              </div>
            )}
            {roadmapCols.map(col => (
              <div key={col.id}
                onDragOver={(e) => { e.preventDefault(); setDragOverCol(col.id); }}
                onDragLeave={() => setDragOverCol(null)}
                onDrop={() => onDrop(col.id)}
                className={`rm-col${dragOverCol === col.id ? ' rm-drag-over' : ''}${dropFlashCol === col.id ? ' rm-drop-flash' : ''}`}
                style={{
                  background: 'var(--bg2)',
                  border: `1px solid ${dragOverCol === col.id ? 'var(--blue)' : 'var(--border)'}`,
                  borderRadius: 12,
                  overflow: 'hidden',
                  boxShadow: dragOverCol === col.id ? '0 0 0 1px var(--bborder) inset' : 'none',
                }}>
                <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 7, height: 7, borderRadius: '50%', background: col.color }} />
                  <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text2)' }}>{col.title}</span>
                  <span style={{ marginLeft: 'auto', fontSize: 10, fontFamily: 'var(--mono)', color: 'var(--text4)' }}>{col.items.length}</span>
                </div>
                <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {col.items.map((item, j) => (
                    <div key={`${col.id}-${j}-${item.title}`} draggable onDragStart={() => onDragStart(col.id, item)} onDragEnd={() => setDraggingCardId(null)} className={`rm-item${draggingCardId === `${col.id}-${item.title}` ? ' rm-item-dragging' : ''}${lastDroppedCardId === `${col.id}-${item.title}` ? ' rm-item-drop-pop' : ''}`} style={{ padding: '8px 10px', background: 'var(--bg3)', borderRadius: 8, fontSize: 11, color: 'var(--text2)' }}>
                      <div className="rm-item-title">{item.title}</div>
                      {item.desc && <div className="rm-item-desc">{item.desc}</div>}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
                        <span className={`td-log-tag ${item.tagC}`}>{item.tag}</span>
                        {item.priority && <span className={`rm-item-priority ${item.priority}`}>{item.priority}</span>}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 6 }}>
                        <div className="proj-av" style={{ marginLeft: 0, width: 20, height: 20, fontSize: 8 }}>{item.assignee}</div>
                        {item.time && <span style={{ fontSize: 9, fontFamily: 'var(--mono)', color: 'var(--text3)' }}>{item.time}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {showAddItem && (
            <div className="overlay open" onClick={(e) => { if (e.target === e.currentTarget) setShowAddItem(false); }}>
              <div className="modal" style={{ width: 460 }}>
                <div className="mhdr">
                  <div>
                    <div className="mttl">Add Roadmap Item</div>
                    <div className="msub">Create a new card in your Kanban board</div>
                  </div>
                  <button className="mclose" onClick={() => setShowAddItem(false)}>×</button>
                </div>
                <div className="mbody">
                  <div className="fgroup">
                    <label className="label">Item title</label>
                    <input className="inp" placeholder="e.g. Implement OAuth login" value={newItem.title} onChange={(e) => setNewItem((p) => ({ ...p, title: e.target.value }))} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <div className="fgroup">
                      <label className="label">Column</label>
                      <select className="sel" value={newItem.column} onChange={(e) => setNewItem((p) => ({ ...p, column: e.target.value as RoadmapColId }))}>
                        {roadmapCols.map(col => <option key={col.id} value={col.id}>{col.title}</option>)}
                      </select>
                    </div>
                    <div className="fgroup">
                      <label className="label">Priority</label>
                      <select className="sel" value={newItem.priority} onChange={(e) => setNewItem((p) => ({ ...p, priority: e.target.value as 'high' | 'medium' | 'low' }))}>
                        <option value="high">High</option>
                        <option value="medium">Medium</option>
                        <option value="low">Low</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="mfoot">
                  <button className="btn-s" onClick={() => setShowAddItem(false)}>Cancel</button>
                  <button className="btn-p" onClick={addRoadmapItem}>Add item</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── AI ── */}
      {tab === 'ai' && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderBottom: '1px solid var(--border)', background: 'var(--bg1)', flexShrink: 0 }}>
            <div className="ai-av-bot" style={{ width: 30, height: 30, lineHeight: '30px', textAlign: 'center', fontSize: 13, flexShrink: 0 }}>S</div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>Aisha</div>
              <div style={{ fontSize: 10, fontFamily: 'var(--mono)', color: 'var(--text3)' }}>Knows your full sprint history</div>
            </div>
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 5 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--green)', boxShadow: '0 0 5px var(--green)' }} />
              <span style={{ fontSize: 10, fontFamily: 'var(--mono)', color: 'var(--green)' }}>Active</span>
            </div>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
            {aiMsgs.map((msg, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flexDirection: msg.role === 'user' ? 'row-reverse' : 'row' }}>
                {msg.role === 'bot'
                  ? <div className="ai-av-bot" style={{ width: 28, height: 28, lineHeight: '28px', textAlign: 'center', fontSize: 12, flexShrink: 0 }}>S</div>
                  : <div className="avr avsm" style={{ background: 'rgba(77,156,248,.18)', color: '#4d9cf8', flexShrink: 0 }}>AK</div>
                }
                <div style={{ maxWidth: 'min(78%, 680px)', minWidth: 0 }}>
                  {msg.role === 'bot' && <div style={{ fontSize: 10, fontFamily: 'var(--mono)', color: 'var(--text3)', marginBottom: 4 }}>Aisha</div>}
                  <div className={msg.role === 'bot' ? 'ai-bubble-bot' : 'ai-bubble-user'} style={{ fontSize: 12.5, lineHeight: 1.6 }}>
                    {msg.role === 'bot' && typeof msg.text === 'string' ? <AiMarkdown content={msg.text} /> : msg.text}
                  </div>
                  {i === 0 && (
                    <div className="ai-action-row">
                      {QUICK.map(a => <button key={a.label} className="ai-action-chip" onClick={() => sendAI(a.q)}>{a.label}</button>)}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {aiThinking && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <div className="ai-av-bot" style={{ width: 28, height: 28, lineHeight: '28px', textAlign: 'center', fontSize: 12, flexShrink: 0 }}>S</div>
                <div className="ai-bubble-bot"><div className="ai-typing"><div className="ai-dot" /><div className="ai-dot" /><div className="ai-dot" /></div></div>
              </div>
            )}
            <div ref={aiEndRef} />
          </div>
          <div style={{ padding: '10px 12px', borderTop: '1px solid var(--border)', background: 'var(--bg1)', flexShrink: 0, display: 'flex', gap: 7 }}>
            <input className="inp" value={aiInput} onChange={e => setAiInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && sendAI()} placeholder="Ask anything about your team…" style={{ flex: 1 }} />
            <button className="btn-p" style={{ padding: '8px 14px', fontSize: 12 }} onClick={() => sendAI()} disabled={aiThinking}>Send</button>
          </div>
        </div>
      )}

      {/* ── CODE SANDBOX ── */}
      {tab === 'sandbox' && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#1e1e1e' }}>
          {/* Header toolbar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', background: '#252526', borderBottom: '1px solid #3c3c3c', flexShrink: 0 }}>
            {/* File Switchers */}
            <div style={{ display: 'flex', gap: 4 }}>
              {Object.keys(sandboxFiles).map(file => (
                <button
                  key={file}
                  onClick={() => setActiveFile(file)}
                  style={{
                    fontSize: 11,
                    fontFamily: 'var(--mono)',
                    padding: '6px 12px',
                    borderRadius: 4,
                    background: activeFile === file ? '#1e1e1e' : 'transparent',
                    border: 'none',
                    color: activeFile === file ? '#ffffff' : '#969696',
                    cursor: 'pointer',
                    fontWeight: activeFile === file ? 600 : 400
                  }}
                >
                  {file}
                </button>
              ))}
            </div>
            {/* AI suggestion button */}
            <button
              onClick={triggerSandboxAI}
              disabled={aiSandboxLoading}
              className="btn-p"
              style={{
                marginLeft: 'auto',
                fontSize: 10,
                padding: '4px 10px',
                background: 'var(--purple)',
                borderColor: 'var(--purple)',
                color: '#fff',
                borderRadius: 4,
                display: 'flex',
                alignItems: 'center',
                gap: 5
              }}
            >
              <svg width="10" height="10" viewBox="0 0 16 16" fill="currentColor">
                <path d="M7.5 0L9 5.5 14.5 7 9 8.5 7.5 14 6 8.5.5 7 6 5.5z"/>
              </svg>
              {aiSandboxLoading ? "Analyzing..." : "AI Optimize Code"}
            </button>
          </div>
          {/* Editor and Preview Area */}
          <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', overflow: 'hidden' }}>
            {/* TextArea Editor */}
            <textarea
              value={sandboxFiles[activeFile]}
              onChange={e => handleSandboxChange(e.target.value)}
              spellCheck={false}
              style={{
                width: '100%',
                height: '100%',
                background: '#1e1e1e',
                color: '#d4d4d4',
                fontFamily: 'var(--mono)',
                fontSize: 13,
                padding: 16,
                border: 'none',
                outline: 'none',
                resize: 'none',
                lineHeight: 1.5,
                tabSize: 2
              }}
            />
            {/* Live iframe sandbox preview */}
            <div style={{ background: '#ffffff', height: '100%', borderLeft: '1px solid #3c3c3c', display: 'flex', flexDirection: 'column' }}>
              <div style={{ padding: '4px 10px', background: '#f3f3f3', borderBottom: '1px solid var(--border)', fontSize: 10, fontFamily: 'var(--mono)', color: 'var(--text3)' }}>
                Live Preview Frame
              </div>
              <iframe
                title="Sandbox Preview"
                srcDoc={`
                  <!DOCTYPE html>
                  <html>
                    <head>
                      <style>
                        body { font-family: sans-serif; padding: 16px; margin: 0; background: #ffffff; color: #333; }
                        ${sandboxFiles['style.css']}
                      </style>
                    </head>
                    <body>
                      ${sandboxFiles['index.html']}
                      <script>
                        try {
                          ${sandboxFiles['app.js']}
                        } catch (err) {
                          console.error(err);
                        }
                      </script>
                    </body>
                  </html>
                `}
                style={{ flex: 1, border: 'none', width: '100%', background: '#ffffff' }}
              />
            </div>
          </div>
        </div>
      )}

      {/* ── WHITEBOARD ── */}
      {tab === 'whiteboard' && <Whiteboard isDemo={!isReal} onStrokeAdded={handleWhiteboardChange} />}

      {/* ── FLOAT HUDDLE WIDGET ── */}
      {inHuddle && huddleState === 'minimized' && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1000,
            background: 'rgba(15, 23, 42, 0.9)',
            backdropFilter: 'blur(16px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 18,
            padding: '8px 16px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            color: '#fff',
          }}
        >
          <span className="live-dot" style={{ background: '#22c55e', width: 6, height: 6, borderRadius: '50%', boxShadow: '0 0 8px #22c55e' }} />
          <span style={{ fontSize: 10, fontWeight: 700, fontFamily: 'var(--mono)' }}>HUDDLE</span>
          <span style={{ fontSize: 10, color: '#94a3b8' }}>Active ({formatHuddleTime(huddleTimer)})</span>
          <button
            onClick={() => setHuddleState('normal')}
            style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', padding: '3px 8px', borderRadius: 6, fontSize: 9, cursor: 'pointer' }}
          >
            Expand
          </button>
          <button
            onClick={toggleHuddle}
            style={{ background: '#ef4444', border: 'none', color: '#fff', padding: '3px 8px', borderRadius: 6, fontSize: 9, cursor: 'pointer', fontWeight: 600 }}
          >
            Leave
          </button>
        </div>
      )}

      {inHuddle && huddleState === 'normal' && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1000,
            background: 'rgba(30, 41, 59, 0.8)',
            backdropFilter: 'blur(16px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 24,
            padding: 16,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.5)',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            width: 280,
            transition: 'all 0.3s',
            color: '#fff',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className="live-dot" style={{ background: '#22c55e', width: 6, height: 6, borderRadius: '50%', boxShadow: '0 0 8px #22c55e' }} />
              <span style={{ fontSize: 11, fontWeight: 700, fontFamily: 'var(--mono)' }}>HUDDLE</span>
              <span style={{ fontSize: 9, color: '#94a3b8', fontFamily: 'var(--mono)' }}>({formatHuddleTime(huddleTimer)})</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <button
                onClick={() => setHuddleState('minimized')}
                style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: 2, display: 'flex', alignItems: 'center' }}
                title="Minimize Huddle"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="5" y1="12" x2="19" y2="12"/></svg>
              </button>
              <button
                onClick={() => setHuddleState('fullscreen')}
                style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: 2, display: 'flex', alignItems: 'center' }}
                title="Fullscreen Zoom Mode"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/></svg>
              </button>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10, background: '#0f172a', borderRadius: 16, padding: 8, minHeight: 120 }}>
            <div style={{ position: 'relative', borderRadius: 12, overflow: 'hidden', aspectRatio: '4/3', background: '#1e293b', border: '1px solid rgba(255,255,255,0.05)' }}>
              {huddleVideo ? (
                <video
                  ref={localVideoRef}
                  autoPlay
                  playsInline
                  muted
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 6 }}>
                  <div className="avr" style={{ background: '#3b82f6', width: 32, height: 32, fontSize: 12 }}>{selfInitials}</div>
                  <span style={{ fontSize: 9, color: '#94a3b8' }}>Camera Off</span>
                </div>
              )}
              <span style={{ position: 'absolute', bottom: 6, left: 6, fontSize: 8, background: 'rgba(15,23,42,0.6)', padding: '2px 6px', borderRadius: 8, fontWeight: 700 }}>
                {selfInitials} (You) {huddleMuted && '🔇'}
              </span>
            </div>

            {isSharingScreen && screenshareStream && (
              <div style={{ position: 'relative', borderRadius: 12, overflow: 'hidden', aspectRatio: '4/3', background: '#1e293b', border: '1px solid rgba(255,255,255,0.05)' }}>
                <video
                  ref={screenVideoRef}
                  autoPlay
                  playsInline
                  muted
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
                <span style={{ position: 'absolute', bottom: 6, left: 6, fontSize: 8, background: 'rgba(239,68,68,0.7)', padding: '2px 6px', borderRadius: 8, fontWeight: 700 }}>
                  🖥️ Sharing Screen
                </span>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 4 }}>
            <button
              onClick={toggleMute}
              style={{ width: 32, height: 32, borderRadius: '50%', border: 'none', background: huddleMuted ? '#ef4444' : 'rgba(255,255,255,0.1)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              title={huddleMuted ? "Unmute Mic" : "Mute Mic"}
            >
              {huddleMuted ? (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="1" y1="1" x2="23" y2="23"/><path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"/><path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
              ) : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
              )}
            </button>

            <button
              onClick={toggleCamera}
              style={{ width: 32, height: 32, borderRadius: '50%', border: 'none', background: huddleVideo ? '#22c55e' : 'rgba(255,255,255,0.1)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              title={huddleVideo ? "Turn Off Camera" : "Turn On Camera"}
            >
              {huddleVideo ? (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 7l-7 5 7 5V7z"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>
              ) : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M16 16v1a2 2 0 0 1-2 2H3a2 2 0 0 1-2 2H10c0 1.66-1.34 3-3 3z"/><path d="M23 7l-7 5 7 5V7z"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
              )}
            </button>

            <button
              onClick={toggleScreenshare}
              style={{ width: 32, height: 32, borderRadius: '50%', border: 'none', background: isSharingScreen ? '#0284c7' : 'rgba(255,255,255,0.1)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              title={isSharingScreen ? "Stop Sharing Screen" : "Share Screen"}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                <line x1="8" y1="21" x2="16" y2="21" />
                <line x1="12" y1="17" x2="12" y2="21" />
              </svg>
            </button>

            <button
              onClick={toggleHuddle}
              style={{ width: 32, height: 32, borderRadius: '50%', border: 'none', background: '#ef4444', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              title="Leave Huddle"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2 2A17 17 0 0 1 3 5a2 2 0 0 1 2-2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L9 11a16 16 0 0 0 1.68 2.31z"/>
              </svg>
            </button>
          </div>

          {/* Real-time AI Transcription Logs */}
          <div className="border-t border-zinc-700/60 pt-3 flex flex-col gap-2">
            <span className="text-[9px] font-black uppercase text-zinc-400 tracking-wider flex items-center gap-1.5">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-indigo-400 animate-pulse">
                <path d="M12 20h9M3 20v-8c0-2.2 1.8-4 4-4h10c2.2 0 4 1.8 4 4v8M3 10V6c0-2.2 1.8-4 4-4h10c2.2 0 4 1.8 4 4v4" />
              </svg>
              <span>AI Huddle Transcript</span>
            </span>
            <div className="bg-slate-950/80 rounded-xl p-2.5 border border-zinc-800/80 max-h-[85px] overflow-y-auto text-[10px] space-y-1.5 custom-scrollbar font-mono leading-relaxed">
              {transcriptions.length === 0 ? (
                <span className="text-zinc-500 italic block py-2 text-center select-none">Listening for speakers…</span>
              ) : (
                transcriptions.map((t, idx) => (
                  <div key={idx} className="text-zinc-300">
                    <strong className={t.sender === 'Aisha' ? 'text-indigo-400' : 'text-emerald-400'}>
                      {t.sender}:
                    </strong>{' '}
                    {t.text}
                  </div>
                ))
              )}
            </div>
            
            {/* Real-time Glowing Audio Waveform Visualizer */}
            <div className="flex items-center justify-center gap-1.5 h-10 w-full bg-slate-950/50 rounded-xl border border-zinc-850 overflow-hidden px-4 mt-2" style={{ boxSizing: 'border-box' }}>
              <span className="text-[7.5px] font-mono font-bold uppercase text-zinc-500 mr-auto shrink-0 select-none">Audio Wave</span>
              <div className="flex items-center gap-[3px] h-6">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((i) => {
                  const randomDelay = `${(i * 0.08).toFixed(2)}s`;
                  const randomHeight = `${Math.floor(Math.random() * 12 + 6)}px`;
                  return (
                    <div
                      key={i}
                      className="w-[2px] bg-gradient-to-t from-cyan-500 to-indigo-500 rounded-full animate-bounce"
                      style={{
                        height: randomHeight,
                        animationDelay: randomDelay,
                        animationDuration: '0.8s',
                        boxShadow: '0 0 6px rgba(6,182,212,0.4)',
                      }}
                    />
                  );
                })}
              </div>
              <span className="text-[7.5px] font-mono text-emerald-450 ml-auto shrink-0 animate-pulse font-bold">CONNECTED</span>
            </div>
            
            {/* AI Sentiment Analysis & Talk-time Tracker */}
            <div className="grid grid-cols-2 gap-3 mt-1.5 pt-2 border-t border-zinc-800/40">
              {/* Left Column: Sentiment Gauge */}
              <div className="bg-slate-950/40 border border-zinc-800/60 rounded-xl p-2 flex flex-col items-center justify-center gap-1">
                <span className="text-[8px] font-black uppercase text-zinc-500 tracking-wider">Huddle Sentiment</span>
                
                {/* SVG Gauge */}
                <div className="relative w-16 h-10 flex items-center justify-center overflow-hidden mt-0.5">
                  <svg className="w-16 h-16 transform -rotate-180" viewBox="0 0 100 100">
                    <circle cx="50" cy="50" r="40" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="12" strokeDasharray="125 125" />
                    <circle 
                      cx="50" 
                      cy="50" 
                      r="40" 
                      fill="none" 
                      stroke={huddleAnalytics.sentiment >= 80 ? '#10b981' : huddleAnalytics.sentiment >= 45 ? '#6366f1' : '#f43f5e'} 
                      strokeWidth="12" 
                      strokeDasharray={`${(huddleAnalytics.sentiment / 100) * 125} 125`} 
                      className="transition-all duration-500"
                    />
                  </svg>
                  <div className="absolute bottom-0 text-[10px] font-mono font-black text-zinc-100">
                    {huddleAnalytics.sentiment}%
                  </div>
                </div>
                <span className={`text-[7.5px] uppercase font-bold tracking-wider text-center ${
                  huddleAnalytics.sentiment >= 80 ? 'text-emerald-450' : huddleAnalytics.sentiment >= 45 ? 'text-indigo-400' : 'text-rose-450'
                }`}>
                  {huddleAnalytics.sentimentLabel}
                </span>
              </div>

              {/* Right Column: Speaker Talk-times */}
              <div className="bg-slate-950/40 border border-zinc-800/60 rounded-xl p-2.5 flex flex-col justify-center gap-2">
                <span className="text-[8px] font-black uppercase text-zinc-500 tracking-wider">Talk Share</span>
                
                <div className="space-y-1.5 text-[8px] font-mono">
                  {/* Local Developer */}
                  <div className="space-y-0.5">
                    <div className="flex justify-between items-center text-zinc-300">
                      <span>You (Aniket)</span>
                      <span>{huddleAnalytics.localPct}%</span>
                    </div>
                    <div className="w-full h-1 bg-zinc-800 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500 transition-all duration-500" style={{ width: `${huddleAnalytics.localPct}%` }} />
                    </div>
                  </div>
                  
                  {/* AI Assistant/Teammates */}
                  <div className="space-y-0.5">
                    <div className="flex justify-between items-center text-zinc-300">
                      <span>Teammates</span>
                      <span>{huddleAnalytics.aiPct}%</span>
                    </div>
                    <div className="w-full h-1 bg-zinc-800 rounded-full overflow-hidden">
                      <div className="h-full bg-indigo-500 transition-all duration-500" style={{ width: `${huddleAnalytics.aiPct}%` }} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {inHuddle && huddleState === 'fullscreen' && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 1100,
            background: '#0b0f19',
            display: 'flex',
            flexDirection: 'column',
            color: '#fff',
            overflow: 'hidden',
          }}
        >
          {/* Immersive Top Bar */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', background: 'rgba(15,23,42,0.6)', borderBottom: '1px solid rgba(255,255,255,0.05)', backdropFilter: 'blur(10px)', flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span className="live-dot" style={{ background: '#22c55e', width: 8, height: 8, borderRadius: '50%', boxShadow: '0 0 10px #22c55e' }} />
              <h3 style={{ fontSize: 13, fontWeight: 700, margin: 0, letterSpacing: '0.5px' }}>IMMERSIVE HUDDLE ROOM</h3>
              <span style={{ fontSize: 11, color: '#94a3b8', background: 'rgba(255,255,255,0.06)', padding: '2px 8px', borderRadius: 8, fontFamily: 'var(--mono)', marginLeft: 8 }}>
                {formatHuddleTime(huddleTimer)}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 11, color: '#22c55e', fontWeight: 600 }}>Active Speaker: {activeSpeaker}</span>
              <button
                onClick={() => setHuddleState('normal')}
                style={{
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  color: '#fff',
                  borderRadius: 8,
                  padding: '4px 12px',
                  fontSize: 10,
                  cursor: 'pointer',
                  fontWeight: 600,
                  transition: 'background 0.2s',
                }}
              >
                Exit Fullscreen
              </button>
            </div>
          </div>

          {/* Immersive Video Canvas Area */}
          <div style={{ flex: 1, display: 'flex', padding: 24, gap: 20, overflow: 'hidden', position: 'relative' }}>
            
            {/* Reactions Overlay */}
            {reactions.length > 0 && (
              <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 100, display: 'flex', flexWrap: 'wrap', gap: 24, padding: 40, justifyContent: 'center', alignItems: 'center' }}>
                {reactions.map(r => (
                  <div key={r.id} style={{
                    animation: 'floatUp 2.5s ease-out forwards',
                    fontSize: 48,
                    background: 'rgba(15,23,42,0.85)',
                    padding: '12px 24px',
                    borderRadius: 20,
                    boxShadow: '0 10px 25px rgba(0,0,0,0.5)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 6
                  }}>
                    <span>{r.emoji}</span>
                    <span style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700 }}>{r.name}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Main Stage (Self / Screenshare / Speaker focus) */}
            <div style={{ flex: 1, background: '#090d16', borderRadius: 24, border: '1px solid rgba(255,255,255,0.05)', overflow: 'hidden', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {isSharingScreen && screenshareStream ? (
                <video
                  ref={fullScreenVideoRef}
                  autoPlay
                  playsInline
                  muted
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
              ) : huddleVideo ? (
                <video
                  ref={fullLocalVideoRef}
                  autoPlay
                  playsInline
                  muted
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                  <div className="avr" style={{ background: '#3b82f6', width: 80, height: 80, fontSize: 24, border: '3px solid rgba(255,255,255,0.1)' }}>{selfInitials}</div>
                  <span style={{ fontSize: 13, color: '#94a3b8' }}>Camera is Off</span>
                </div>
              )}

              {/* Pulsing Active Speaker neon glow */}
              {activeSpeaker === 'You' && (
                <div style={{ position: 'absolute', inset: 0, border: '3px solid #22c55e', borderRadius: 24, pointerEvents: 'none', boxShadow: 'inset 0 0 20px rgba(34,197,94,0.4)', animation: 'speakerPulse 2s infinite ease-in-out' }} />
              )}

              <span style={{ position: 'absolute', bottom: 16, left: 16, fontSize: 10, background: 'rgba(15,23,42,0.7)', padding: '4px 10px', borderRadius: 8, fontWeight: 700 }}>
                {selfInitials} (You) {huddleMuted && '🔇'}
              </span>
            </div>

            {/* Gallery Sidebar for other participants */}
            <div style={{ width: 220, display: 'flex', flexDirection: 'column', gap: 16, flexShrink: 0, overflowY: 'auto' }}>
              {otherActiveMembers.length === 0 ? (
                <div style={{ padding: '16px 12px', textAlign: 'center', background: '#090d16', borderRadius: 16, border: '1px solid rgba(255,255,255,0.05)', fontSize: 10, color: '#94a3b8' }}>
                  No other teammates in huddle
                </div>
              ) : (
                otherActiveMembers.map((m, idx) => {
                  const initials = m.initials || m.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
                  const isSpeaking = activeSpeaker === m.name;
                  return (
                    <div
                      key={m.id || m.userId || idx}
                      style={{
                        position: 'relative',
                        height: 130,
                        background: '#090d16',
                        borderRadius: 16,
                        border: isSpeaking ? '2.5px solid #22c55e' : '1px solid rgba(255,255,255,0.05)',
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: isSpeaking ? '0 0 15px rgba(34,197,94,0.3)' : 'none',
                        transition: 'all 0.3s'
                      }}
                    >
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                        <div className="avr" style={{ background: MBG[initials] || 'var(--purple)', width: 44, height: 44, fontSize: 14 }}>{initials}</div>
                        <span style={{ fontSize: 10, color: '#e2e8f0', fontWeight: 600 }}>{m.name}</span>
                      </div>
                      {isSpeaking && (
                        <div style={{ position: 'absolute', top: 8, right: 8, background: '#22c55e', borderRadius: '50%', width: 6, height: 6 }} />
                      )}
                      <span style={{ position: 'absolute', bottom: 8, left: 8, fontSize: 8, color: '#94a3b8' }}>
                        {isSpeaking ? '🎙️ Speaking' : '🎙️ Muted'}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Immersive Glassmorphic Controls Bar */}
          <div style={{ padding: '20px 24px', display: 'flex', justifyContent: 'center', background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(10px)', borderTop: '1px solid rgba(255,255,255,0.05)', flexShrink: 0, position: 'relative' }}>
            
            {/* Reaction picker popover */}
            {showReactionPicker && (
              <div style={{ position: 'absolute', bottom: 74, background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 16, padding: '10px 16px', display: 'flex', gap: 12, boxShadow: '0 10px 25px rgba(0,0,0,0.5)', zIndex: 100 }}>
                {['👏', '❤️', '😂', '🎉', '😮', '👋'].map(emoji => (
                  <button
                    key={emoji}
                    onClick={() => triggerReaction(emoji)}
                    style={{ fontSize: 20, background: 'none', border: 'none', cursor: 'pointer', transition: 'transform 0.15s' }}
                    className="react-emoji-btn"
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'rgba(255,255,255,0.06)', padding: '6px 20px', borderRadius: 30, border: '1px solid rgba(255,255,255,0.08)' }}>
              {/* Mic toggle */}
              <button
                onClick={toggleMute}
                style={{ background: huddleMuted ? '#ef4444' : 'rgba(255,255,255,0.08)', color: '#fff', border: 'none', width: 38, height: 38, borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                title={huddleMuted ? "Unmute Mic" : "Mute Mic"}
              >
                {huddleMuted ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="1" y1="1" x2="23" y2="23"/><path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"/><path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
                )}
              </button>

              {/* Video toggle */}
              <button
                onClick={toggleCamera}
                style={{ background: huddleVideo ? '#22c55e' : 'rgba(255,255,255,0.08)', color: '#fff', border: 'none', width: 38, height: 38, borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                title={huddleVideo ? "Turn Off Camera" : "Turn On Camera"}
              >
                {huddleVideo ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 7l-7 5 7 5V7z"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 16v1a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2v1"/><path d="M23 7l-7 5 7 5V7z"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                )}
              </button>

              {/* Screen share toggle */}
              <button
                onClick={toggleScreenshare}
                style={{ background: isSharingScreen ? '#0284c7' : 'rgba(255,255,255,0.08)', color: '#fff', border: 'none', width: 38, height: 38, borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                title={isSharingScreen ? "Stop Sharing Screen" : "Share Screen"}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                  <line x1="8" y1="21" x2="16" y2="21" />
                  <line x1="12" y1="17" x2="12" y2="21" />
                </svg>
              </button>

              {/* Reactions trigger */}
              <button
                onClick={() => setShowReactionPicker(!showReactionPicker)}
                style={{ background: 'rgba(255,255,255,0.08)', color: '#fff', border: 'none', width: 38, height: 38, borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                title="Send Reaction"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><circle cx="9" cy="9" r="1" fill="currentColor"/><circle cx="15" cy="9" r="1" fill="currentColor"/></svg>
              </button>

              {/* Leave Call */}
              <button
                onClick={toggleHuddle}
                style={{ background: '#ef4444', color: '#fff', border: 'none', width: 38, height: 38, borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                title="Leave Huddle"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2 2A17 17 0 0 1 3 5a2 2 0 0 1 2-2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L9 11a16 16 0 0 0 1.68 2.31z"/>
                </svg>
              </button>
            </div>
          </div>

          {/* Inject Float Animation styles */}
          <style>{`
            @keyframes floatUp {
              0% { transform: translateY(50px) scale(0.6); opacity: 0; }
              10% { opacity: 1; }
              90% { opacity: 1; }
              100% { transform: translateY(-200px) scale(1.1); opacity: 0; }
            }
            @keyframes speakerPulse {
              0% { box-shadow: inset 0 0 20px rgba(34,197,94,0.4); opacity: 0.8; }
              50% { box-shadow: inset 0 0 35px rgba(34,197,94,0.75); opacity: 1; }
              100% { box-shadow: inset 0 0 20px rgba(34,197,94,0.4); opacity: 0.8; }
            }
          `}</style>
        </div>
      )}
    </div>
  );
}
